import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { Cell, Edge, MapData, Region } from '../map/types.js';
import { makeRng } from '../map/rng.js';
import {
  BIOME_DECO_DENSITY,
  GRASS_FRAMES_DOUBLE,
  GRASS_FRAMES_SIMPLE,
  pingPongFrames,
} from './decorations.js';

/**
 * Game-layer entity placed on the map (castle, army stack, future banners…).
 * The renderer doesn't know what each one means; it just shows the sprite at
 * the requested world position.
 */
export interface MapEntity {
  /** Unique id (currently informational; future: enables diff-based updates). */
  id: string;
  /** Game-layer tag used for click handling and animation. */
  kind?: 'castle' | 'army';
  /** The region this entity sits on — emitted with click events. */
  regionId?: number;
  /** World coords: [x, z] where z is the original 2D y. */
  position: [number, number];
  /** Path served by Vite, e.g. '/sprites/chateau_tier1.png'. */
  spriteUrl?: string;
  /** Pre-built texture (e.g. a CanvasTexture composited from multiple sprites). */
  texture?: THREE.Texture;
  /** Vertical world-units scale (default 30). The HEIGHT of the sprite quad. */
  scale?: number;
  /** Width / height ratio. 1 = square. Used for non-square composite textures. */
  aspect?: number;
  /** Extra Y lift above the platform (default 0 → flush with the top face). */
  yOffset?: number;
  /** Render order — higher draws on top. Armies want > castles + > terrain. */
  renderOrder?: number;
  /** Idle "impatient" sway + squash animation (used for armies). */
  wobble?: boolean;
  /** Tint multiplier on the texture (0xffffff = no change). Used to dim
   *  armies the local player can't act on. */
  tint?: number;
  /**
   * Optional "badge" sprites placed around the main entity at 50% scale.
   * Each satellite has its own texture + relative offset (in world units
   * from the main sprite's base position). Up to 5 satellites is the
   * intended maximum; the renderer doesn't enforce it.
   */
  satellites?: Array<{
    texture: THREE.Texture;
    aspect?: number;
    /** Relative XZ offset in world units (X horizontal, Z away from camera). */
    offset: [number, number];
  }>;
}

/**
 * Bookkeeping for a persistent entity sprite (castle / army). Lives in the
 * MapRenderer.entities registry and is mutated in place across state patches,
 * which keeps wobble phase + any in-flight tweens continuous.
 */
interface EntityInstance {
  sprite: THREE.Sprite;
  kind: 'castle' | 'army';
  regionId: number;
  /** Resting (untweened, unwobbled) position in world coordinates. */
  baseX: number;
  baseY: number;
  baseZ: number;
  baseScaleX: number;
  baseScaleY: number;
  wobble: boolean;
  /** Random phase offset so a population doesn't pulse in sync. */
  phase: number;
  /** Small satellite badges (one sprite each) glued to the main entity. */
  satellites: THREE.Sprite[];
  /**
   * Smooth-tint state. The displayed color (`tintCurrent`) lerps toward
   * `tintTarget` once the clock passes `tintStartTime`, over `tintDuration`
   * seconds. Used to delay the "this army is spent" graying until AFTER the
   * ghost has landed.
   */
  tintCurrent: number;
  tintTarget: number;
  tintStartTime: number;
  tintDuration: number;
  /**
   * Opacity transition (same lerp pattern as tint). Used to hide the
   * destination entity during a ghost flight, then fade it in once the
   * flight has settled — otherwise the player sees two sprites in the
   * target territory (the freshly-arrived "real" entity AND the incoming
   * ghost). Range 0..1.
   */
  opacityFrom: number;
  opacityTarget: number;
  opacityStartTime: number;
  opacityDuration: number;
  /**
   * Knockback / hit-reaction state. Set by `knockEntity()` when the
   * army takes an arrow volley but survives. During the knock window
   * `updateWobble` :
   *   • offsets the sprite in (knockDirX, knockDirZ) direction
   *   • flashes the tint toward bright white
   *   • adds a stutter scale punch
   * Reverts to zero once `knockStart + knockDuration` has elapsed.
   * knockStart = 0 means "no active knock".
   */
  knockStart: number;
  knockDuration: number;
  knockDirX: number;
  knockDirZ: number;
}

/**
 * One-shot animated sprite. Used to visualise an army moving from region A
 * to region B without disturbing the entity registry: the source army's
 * registry entry is updated immediately to reflect the new state, and a
 * ghost is spawned over the visible motion so the player sees "something
 * arrived from that direction".
 */
/**
 * A simple physics-driven particle. Either parabolic (gravity > 0) for impact
 * confetti, or linear (gravity = 0) for projectiles travelling in a straight
 * line over the terrain.
 */
interface ParticleInstance {
  sprite: THREE.Sprite;
  startTime: number;
  duration: number; // seconds
  /** World position at startTime. */
  startPos: [number, number, number];
  /** World units / second. */
  velocity: [number, number, number];
  /** Acceleration applied to Y (world units / s²). Negative = falling. */
  gravity: number;
  /** Sprite scale at startTime (used as base). */
  startScale: number;
  /** Sprite scale at duration's end (linear interp). */
  endScale: number;
  /** Optional X-axis sway amplitude (world units). 0 = no sway. */
  swayAmplitude?: number;
  /** Optional sway frequency in Hz. */
  swayFreq?: number;
  /** If true, only Y is squashed (scale.y → 0) and X stays at startScale.
   *  Used for the "flattened corpse" death effect. */
  squashOnly?: boolean;
  /** If true, the sprite respects its texture's aspect ratio for sizing. */
  aspect?: number;
  /** Optional fixed rotation (radians) applied each frame. Used for arrow
   *  projectiles where the sprite must keep pointing in the horizontal
   *  flight direction throughout the arc. */
  fixedRotation?: number;
}

interface GhostInstance {
  sprite: THREE.Sprite;
  /** clock.elapsedTime at spawn. */
  startTime: number;
  /** seconds */
  duration: number;
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
  /** Resting Y — the ghost arcs above this during flight. */
  y: number;
  /** Peak hop height in world units (arc). */
  hopHeight: number;
  /** Called once when the tween finishes (sprite about to despawn). */
  onComplete?: () => void;
  /** Optional dust-trail colour (0xRRGGBB). When set, `updateGhosts`
   *  emits a small colour pixel under the sprite at regular intervals
   *  for the "marching feet kicking up dust" effect. */
  trailColor?: number;
  /** Internal: clock time of the last trail particle emission. */
  lastTrailEmit?: number;
}

interface GrassInstance {
  sprite: THREE.Sprite;
  frames: THREE.Texture[];
  /** seconds per frame (varies per instance for desync) */
  frameTime: number;
  timeAccum: number;
  currentFrame: number;
  /** Region the tuft belongs to. Used by `setOwnership` so we can
   *  re-tint live grass when a region's owner changes (capture, etc.) —
   *  without this the tuft would freeze to the colour it had at first
   *  placement, even after the kingdom flipped. */
  regionId: number;
  /** Biome's "wild" colour for this tuft — fallback used when the
   *  region is unowned. Cached so we don't have to look it up again
   *  on every ownership patch. */
  baseColorHex: number;
}

/**
 * Each grass PNG is 260×260 with most pixels transparent. The actual tuft
 * occupies a small region in the lower-centre. Without cropping, a sprite
 * scaled to ~5 world units shows ~0.5 world units of visible grass — invisible
 * on screen. We crop the sample area via texture UV offset/repeat so a sprite
 * of size N world units is N world units of *grass*, not of empty canvas.
 *
 * Bounds are the UNION of all 5 animation frames so the visible area stays
 * stable through the ping-pong loop (no jitter).
 */
const GRASS_SOURCE_SIZE = 260;
const GRASS_SIMPLE_CROP = { x: 90, y: 180, w: 60, h: 60 };
const GRASS_DOUBLE_CROP = { x: 60, y: 170, w: 90, h: 70 };
const GRASS_SIMPLE_ASPECT = GRASS_SIMPLE_CROP.w / GRASS_SIMPLE_CROP.h;
const GRASS_DOUBLE_ASPECT = GRASS_DOUBLE_CROP.w / GRASS_DOUBLE_CROP.h;

function applyGrassCrop(
  tex: THREE.Texture,
  crop: { x: number; y: number; w: number; h: number },
): void {
  tex.offset.x = crop.x / GRASS_SOURCE_SIZE;
  // flipY=true (default): image y=0 maps to v=1, so image y goes "down" but v
  // goes "up". The bottom of the cropped rectangle (image y = crop.y+crop.h)
  // maps to the SMALLER v value.
  tex.offset.y = (GRASS_SOURCE_SIZE - crop.y - crop.h) / GRASS_SOURCE_SIZE;
  tex.repeat.x = crop.w / GRASS_SOURCE_SIZE;
  tex.repeat.y = crop.h / GRASS_SOURCE_SIZE;
}

/**
 * 2.5D map renderer (Three.js).
 *
 * World axes:
 *   X = horizontal of the original 2D map
 *   Z = vertical of the original 2D map
 *   Y = elevation (terrain platforms grow upward, ocean is below)
 *
 * Camera is orthographic, locked at a 45° tilt. OrbitControls is wired so:
 *   - left-drag  = pan  (along the world XZ plane → the camera glides over the
 *                        ground rather than across the screen)
 *   - right-drag = rotate (azimuth only — polar tilt is locked)
 *   - wheel      = zoom (orthographic frustum scaling)
 *
 * Each land cell becomes a flat platform at its biome height. Where two
 * neighbouring cells have different heights, a vertical wall is generated so
 * the scene reads like Final Fantasy Tactics terrain.
 */

const OCEAN_FLOOR_Y = -4;

/**
 * World-space size of one tile of the parchment-grain texture. Smaller value =
 * more tile repeats per territory = the grain looks finer. Independent from
 * the texture's native pixel size — only ratios in world space matter.
 */
const TERRAIN_TILE_WORLD_SIZE = 150;

/** Per-biome platform height. Tweak to taste. */
const BIOME_HEIGHT: Record<string, number> = {
  ocean: OCEAN_FLOOR_Y,
  coast: 1,
  beach: 1,
  plains: 4,
  forest: 6,
  deepforest: 8,
  desert: 4,
  swamp: 2,
  hills: 14,
  mountain: 28,
  snow: 32,
};

function cellY(c: Cell): number {
  if (c.isOcean) return OCEAN_FLOOR_Y;
  return BIOME_HEIGHT[c.biome] ?? 4;
}

/**
 * Draw a cat paw print (one big palm pad + four toe beans) onto a 64×64
 * canvas in pure white, returned as a NearestFilter CanvasTexture. The
 * capture-stamp sprite tints this white shape to the conqueror's colour.
 */
function buildPawTexture(): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = 64;
  cv.height = 64;
  const ctx = cv.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    const ellipse = (x: number, y: number, rx: number, ry: number): void => {
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    };
    // Palm pad — a rounded triangle-ish blob in the lower middle.
    ellipse(32, 40, 15, 13);
    // Four toe beans across the top, slightly fanned.
    ellipse(15, 22, 6.5, 8);
    ellipse(26, 15, 6.5, 8.5);
    ellipse(38, 15, 6.5, 8.5);
    ellipse(49, 22, 6.5, 8);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export class MapRenderer {
  private host: HTMLElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private controls: OrbitControls | null = null;
  /** Active camera tween (controls.target). Null when no animation is in flight. */
  private cameraTween: {
    fromX: number; fromZ: number;
    toX: number; toZ: number;
    startTime: number;
    duration: number;
  } | null = null;

  private fondTexture: THREE.Texture | null = null;
  private map: MapData | null = null;

  private terrainMesh: THREE.Mesh | null = null;
  private coastLines: THREE.LineSegments | null = null;
  private innerLines: THREE.LineSegments | null = null;
  private oceanMesh: THREE.Mesh | null = null;
  /** Reference to the ocean ShaderMaterial so the loop can tick `uTime`. */
  private oceanMaterial: THREE.ShaderMaterial | null = null;
  /** Baked distance-from-coast texture (R = clamp(dist / uCoastMax, 0, 1) * 255).
   *  Sampled by the ocean shader to draw the sand apron + animated foam wave
   *  fronts radiating outward from continents. */
  private coastDistanceTex: THREE.DataTexture | null = null;
  /** Texture for the snacalia (jumping fish/eel) sprite. */
  private snacaliaTexture: THREE.Texture | null = null;
  /** Active snacalia jump animations. */
  private snacalias: Array<{
    sprite: THREE.Sprite;
    startTime: number;
    duration: number;
    fromX: number;
    fromZ: number;
    toX: number;
    toZ: number;
    apexY: number;
    baseScale: number;
    wobbleFreq: number;
    splashedTakeoff: boolean;
  }> = [];
  /** Next clock time we'll try to spawn a snacalia (sec). */
  private nextSnacaliaSpawnAt = 0;

  /** Texture for the flying-leaf sprite. */
  private leafTexture: THREE.Texture | null = null;
  /** Active leaf instances drifting across the map. */
  private leaves: Array<{
    sprite: THREE.Sprite;
    startTime: number;
    duration: number;
    /** World-space start position (x, z). */
    fromX: number;
    fromZ: number;
    /** Constant horizontal wind velocity (world units / sec). */
    vx: number;
    vz: number;
    /** Vertical bob frequency (rad/sec) + amplitude (world units). */
    bobFreq: number;
    bobAmp: number;
    /** Y the leaf hovers at. */
    baseY: number;
    /** Sprite rotation rate (rad/sec) — tumbling in the wind. */
    rotRate: number;
    rotPhase: number;
  }> = [];
  /** Next clock time we'll try to spawn a leaf (sec). */
  private nextLeafSpawnAt = 0;
  /** Toggle: each spawn alternates this so leaves come in dark-green /
   *  ocean-blue pairs (no two consecutive leaves of the same colour). */
  private leafColorToggle = false;
  /** Global wind angle (radians) — every leaf drifts along this same
   *  direction so the breeze reads as a coherent wind, not a swarm
   *  of leaves going every which way.
   *
   *  Fixed at "left → right, tilted ~10° toward the top of the
   *  screen": with the orthographic camera looking down from +Y/+Z
   *  the screen-X axis is world +X (so positive = right) and the
   *  screen-Y "up" axis maps to world -Z (so the tilt term is
   *  negative Z). Hence `angle = -10°` (-π/18). All leaves use this
   *  same heading; only their per-leaf speed varies. */
  private leafWindAngle: number = -10 * Math.PI / 180;
  /** Expanding water-ring meshes (takeoff/landing of snacalia, etc.). */
  private rippleRings: Array<{
    mesh: THREE.Mesh;
    startTime: number;
    duration: number;
    startRadius: number;
    endRadius: number;
  }> = [];
  private hoverLines: THREE.LineSegments | null = null;

  private hoveredRegion: Region | null = null;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  /** regionId → hex color override. When set, fills override biome colour. */
  private ownershipColors = new Map<number, number>();
  /** Listeners notified when the user clicks (not drags) on a region. */
  private clickListeners = new Set<(regionId: number) => void>();
  /** Same as clickListeners, but for right-click (button=2). */
  private rightClickListeners = new Set<(regionId: number) => void>();
  /** Fired when the user clicks on a castle/army sprite (instead of the terrain). */
  private entityClickListeners = new Set<(info: { kind: 'castle' | 'army'; regionId: number }) => void>();
  /** Fired when the user left-clicks somewhere that ISN'T a land region —
   *  ocean tiles, or off-map. Lets the game layer dismiss its open panels. */
  private emptyClickListeners = new Set<() => void>();
  /** Persistent outline group used by setActionHighlights. Pulses when active. */
  /** Hides queued for entities that may not exist yet (state hasn't arrived).
   *  Keyed by regionId. Consumed by setMapEntities when a matching entity is
   *  created. Cleared as side-effect when an entity is created or expires. */
  private pendingHides = new Map<number, { until: number }>();
  private actionHighlightLines: THREE.LineSegments | null = null;
  /** Black "shadow" line drawn under `actionHighlightLines`. The colored line
   *  is dashed, so where its gaps fall the black underneath shows through,
   *  producing the alternating dashed black + faction color border. */
  private actionHighlightBlackLines: THREE.LineSegments | null = null;
  /** Translucent fill mesh covering the tops of the highlighted regions. */
  private actionHighlightFill: THREE.Mesh | null = null;
  /** Thick solid-colour ribbon hugging the OUTER perimeter of the
   *  highlighted set. The dashed line + black underline are accent
   *  details; this ribbon is the high-visibility "where can I go"
   *  outline in the player's banner colour. */
  private actionHighlightOuterBorder: THREE.Mesh | null = null;
  /** Whether the highlight currently has geometry — drives the blink animation. */
  private actionHighlightActive = false;
  /** Cached alpha sampler per texture for per-pixel hit testing of sprites. */
  private alphaSamplers = new Map<THREE.Texture, (u: number, v: number) => number>();
  /** Pixel-space mousedown position; used to differentiate clicks from drags. */
  private downPos: { x: number; y: number; t: number } | null = null;
  private downPosRight: { x: number; y: number; t: number } | null = null;

  /** Animated billboards (grass tufts). Updated in the render loop. */
  private grassInstances: GrassInstance[] = [];
  /** Cache of grass frame textures (loaded once in init). */
  private spriteCache = new Map<string, THREE.Texture>();
  /** Drives per-frame deltaTime + an elapsedTime cursor for grass /
   *  ghost / clash / particle animations.
   *
   *  We use a small inline class rather than `new THREE.Clock()` —
   *  Three.js r184+ prints a deprecation warning every time Clock is
   *  instantiated ("Please use THREE.Timer instead"). Same API
   *  (`elapsedTime` + `getDelta()`) backed by `performance.now()`,
   *  so the rest of the renderer is unchanged. */
  private clock = (() => {
    const start = performance.now();
    let last = start;
    return {
      get elapsedTime(): number { return (performance.now() - start) / 1000; },
      getDelta(): number {
        const now = performance.now();
        const dt = (now - last) / 1000;
        last = now;
        return dt;
      },
    };
  })();
  /**
   * Persistent entity registry, keyed by stable id ("army-42", "castle-3").
   * Diff-based updates: only sprites that disappear/appear are destroyed or
   * created; the rest are mutated in place so animation state (wobble phase,
   * tween, baseX/baseScaleX, …) survives across state patches.
   */
  private entities = new Map<string, EntityInstance>();
  /** Short-lived sprites used for movement animations. Run their tween then
   *  remove themselves from the scene. */
  private ghosts: GhostInstance[] = [];
  /** Active melee-clash instances. Each one shows two faction sprites
   *  side-by-side, lunging at each other while emitting impact bursts +
   *  faction-coloured pixels. Used for combat outcomes after the ghost
   *  lands. */
  private clashInstances: Array<{
    attackerSp: THREE.Sprite;
    defenderSp: THREE.Sprite;
    startTime: number;
    duration: number;
    centerX: number;
    centerZ: number;
    baseY: number;
    attackerColor: number;
    defenderColor: number;
    lastImpactT: number;
  }> = [];
  /** Confetti-style particles for impact bursts + projectiles. */
  private particles: ParticleInstance[] = [];
  /** Active "Pattée spéciale" drop sprites — fall from sky then bob. */
  private drops: Array<{
    sprite: THREE.Sprite;
    x: number;
    z: number;
    restingY: number;
    startTime: number;
    /** Time the drop landed. 0 = still falling. */
    landedAt: number;
  }> = [];
  /** Active boat animations (barbarian raid arrival). Each boat sails
   *  in a straight line from `fromX,fromZ` (an ocean point computed
   *  from the landing region) to `toX,toZ` (the landing centroid)
   *  over `duration` seconds, then auto-removes itself.
   *  When `followCamera` is true the camera target tracks the sprite
   *  every frame so the player sees the boat in flight. */
  private boats: Array<{
    sprite: THREE.Sprite;
    fromX: number;
    fromZ: number;
    toX: number;
    toZ: number;
    startTime: number;
    duration: number;
    followCamera: boolean;
    /** Optional callback fired exactly once when the boat finishes
     *  its trip (right before it's removed from the scene). */
    onDone?: () => void;
    doneFired: boolean;
  }> = [];
  /** Tiny white square used as the source texture for every particle. We
   *  tint per instance via material.color so a single texture supports any
   *  palette (faction colors, white flashes, etc.). */
  private whiteSquareTex: THREE.CanvasTexture | null = null;
  /** Soft radial blob for foam (coastline + boat wake). */
  private foamTex: THREE.CanvasTexture | null = null;
  /** Paw-print texture for the capture stamp. */
  private pawTex: THREE.CanvasTexture | null = null;

  /** Active "capture wave" overlays — a colour flood that sweeps across a
   *  freshly conquered region from the conquering edge. Each entry owns a
   *  ShaderMaterial whose `uProgress` we animate 0→1, then fade out. */
  private captureWaves: Array<{
    mesh: THREE.Mesh;
    material: THREE.ShaderMaterial;
    startTime: number;
    duration: number;
    fadeStart: number;
  }> = [];
  /** Paw-print stamps dropped on conquered tiles. Scale up + fade out. */
  private pawStamps: Array<{
    sprite: THREE.Sprite;
    startTime: number;
    duration: number;
    baseScale: number;
  }> = [];
  /** Persistent waving faction flags on castles, keyed by regionId so we
   *  can reconcile (add/remove/recolour) across ownership patches. */
  private castleFlags = new Map<number, {
    sprite: THREE.Sprite;
    baseX: number;
    baseY: number;
    baseZ: number;
    phase: number;
  }>();
  /** Per-boat timestamp of the last wake puff spawned, so the wake emits
   *  at a fixed cadence rather than every frame. Keyed by boat index. */
  private lastWakeAt: number[] = [];

  /** Reserved for future debug overlays. */
  debug = false;

  constructor(host: HTMLElement) {
    this.host = host;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(host.clientWidth, host.clientHeight);
    // Clear colour matches the ocean shader's mid-deep band so the canvas
    // bg blends with the in-world ocean if the camera ever sees a slice
    // beyond the plane.
    this.renderer.setClearColor(0x3267a0, 1);
    // Three.js r155+ outputs sRGB by default; explicit for safety.
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(this.renderer.domElement);
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault());

    this.scene = new THREE.Scene();
  }

  async init(): Promise<void> {
    const loader = new THREE.TextureLoader();

    // Territory background
    this.fondTexture = await loader.loadAsync('/sprites/fond_region.png');
    this.fondTexture.colorSpace = THREE.SRGBColorSpace;
    this.fondTexture.wrapS = THREE.RepeatWrapping;
    this.fondTexture.wrapT = THREE.RepeatWrapping;
    this.fondTexture.magFilter = THREE.NearestFilter;
    this.fondTexture.minFilter = THREE.NearestFilter;

    // All grass frames (simple + double, 5 frames each). Each texture also
    // gets a UV crop applied so it samples only the visible tuft area.
    const loadGrassFrame = async (name: string, isDouble: boolean): Promise<void> => {
      const url = `/sprites/${name}`;
      const tex = await loader.loadAsync(url);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      applyGrassCrop(tex, isDouble ? GRASS_DOUBLE_CROP : GRASS_SIMPLE_CROP);
      this.spriteCache.set(url, tex);
    };
    await Promise.all([
      ...GRASS_FRAMES_SIMPLE.map((n) => loadGrassFrame(n, false)),
      ...GRASS_FRAMES_DOUBLE.map((n) => loadGrassFrame(n, true)),
    ]);

    // Build the white-square texture used by every particle instance.
    // Drawn on an 8×8 canvas with NearestFilter for crisp tinted squares.
    const partCanvas = document.createElement('canvas');
    partCanvas.width = 8;
    partCanvas.height = 8;
    const partCtx = partCanvas.getContext('2d');
    if (partCtx) {
      partCtx.fillStyle = '#ffffff';
      partCtx.fillRect(0, 0, 8, 8);
    }
    this.whiteSquareTex = new THREE.CanvasTexture(partCanvas);
    this.whiteSquareTex.magFilter = THREE.NearestFilter;
    this.whiteSquareTex.minFilter = THREE.NearestFilter;
    this.whiteSquareTex.colorSpace = THREE.SRGBColorSpace;

    // Soft radial "puff" texture for foam (coastline + boat wake) — a
    // white blob with a soft alpha falloff so foam reads as froth, not
    // hard squares. 32×32 with a radial gradient.
    const foamCanvas = document.createElement('canvas');
    foamCanvas.width = 32;
    foamCanvas.height = 32;
    const foamCtx = foamCanvas.getContext('2d');
    if (foamCtx) {
      const grad = foamCtx.createRadialGradient(16, 16, 0, 16, 16, 16);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.55, 'rgba(235,245,255,0.7)');
      grad.addColorStop(1, 'rgba(220,238,255,0)');
      foamCtx.fillStyle = grad;
      foamCtx.fillRect(0, 0, 32, 32);
    }
    this.foamTex = new THREE.CanvasTexture(foamCanvas);
    this.foamTex.colorSpace = THREE.SRGBColorSpace;

    // Paw-print texture (palm pad + 4 toe beans) used for the capture
    // stamp — cat-themed mark dropped on a freshly conquered tile.
    this.pawTex = buildPawTexture();

    // Snacalia (jumping fish/eel) — kept here so spawnSnacalia() can fire
    // any time without a per-spawn async hop.
    try {
      this.snacaliaTexture = await loader.loadAsync('/sprites/animal_snacalia.png');
      this.snacaliaTexture.colorSpace = THREE.SRGBColorSpace;
      this.snacaliaTexture.magFilter = THREE.NearestFilter;
      this.snacaliaTexture.minFilter = THREE.NearestFilter;
    } catch {
      this.snacaliaTexture = null;   // graceful fallback if the asset is missing
    }

    // Flying leaves — pre-loaded once so spawnLeaf() can fire cheap.
    try {
      this.leafTexture = await loader.loadAsync('/sprites/deco_feuille.png');
      this.leafTexture.colorSpace = THREE.SRGBColorSpace;
      this.leafTexture.magFilter = THREE.NearestFilter;
      this.leafTexture.minFilter = THREE.NearestFilter;
    } catch {
      this.leafTexture = null;
    }

    window.addEventListener('resize', () => this.handleResize());
    // Mobile rotation portrait ↔ landscape doesn't always fire
    // `resize` reliably on every browser; `orientationchange` plus
    // a 1-frame debounce covers the gap. Both call the same handler.
    window.addEventListener('orientationchange', () => {
      requestAnimationFrame(() => this.handleResize());
    });
    this.renderer.domElement.addEventListener('pointermove', (e) => this.handlePointer(e));
    // Click vs drag detection — needed because OrbitControls eats most pointer
    // movement; only mouseups within ~5px of mousedown count as a click. Left
    // (button 0) and right (button 2) emit independent events.
    this.renderer.domElement.addEventListener('pointerdown', (e) => {
      const rec = { x: e.clientX, y: e.clientY, t: performance.now() };
      if (e.button === 2) this.downPosRight = rec;
      else if (e.button === 0) this.downPos = rec;
    });
    this.renderer.domElement.addEventListener('pointerup', (e) => {
      if (e.button === 2) this.handleClick(e, true);
      else if (e.button === 0) this.handleClick(e, false);
    });

    this.startLoop();
  }

  /**
   * Replace the per-region fill colour overrides. Call this with the current
   * ownership map (regionId → hex colour) and the scene rebuilds.
   */
  setOwnership(overrides: Map<number, number>): void {
    this.ownershipColors = overrides;
    if (this.map) {
      // Only the terrain mesh + lines need rebuilding (vertex colors changed).
      // Entities, ghosts, particles, grass — all transient or independent —
      // MUST survive this call, otherwise mid-flight animations get nuked
      // every time an ownership flip happens (which is every state patch).
      this.disposeTerrainOnly();
      this.buildScene();
      // Grass survives the rebuild, but its tint was baked at placement
      // time from the OLD ownership. Repaint each tuft from the fresh
      // colour map so a region capture immediately recolours its grass
      // instead of leaving the previous owner's tint visible.
      this.retintGrass();
    }
  }

  /**
   * Walk every live grass sprite and reset its colour to match the
   * current ownership. Cheap (a per-instance `material.color.setHex`
   * call). Falls back to the biome's base colour when a region is
   * unowned, exactly like the original placement-time computation.
   */
  private retintGrass(): void {
    if (this.grassInstances.length === 0) return;
    const tmp = new THREE.Color();
    for (const g of this.grassInstances) {
      const ownerHex = this.ownershipColors.get(g.regionId);
      tmp.setHex(ownerHex ?? g.baseColorHex, THREE.SRGBColorSpace);
      tmp.multiplyScalar(0.85);
      const mat = g.sprite.material as THREE.SpriteMaterial;
      mat.color.copy(tmp);
    }
  }

  /** Subscribe to "left-click on a land region" events. Returns an unsubscribe fn. */
  onRegionClick(fn: (regionId: number) => void): () => void {
    this.clickListeners.add(fn);
    return () => this.clickListeners.delete(fn);
  }

  /** Subscribe to "click on nothing" — left clicks that hit ocean or
   *  miss the map entirely. Returns an unsubscribe function. */
  onEmptyClick(fn: () => void): () => void {
    this.emptyClickListeners.add(fn);
    return () => this.emptyClickListeners.delete(fn);
  }

  /** Subscribe to "right-click on a land region" events. */
  onRegionRightClick(fn: (regionId: number) => void): () => void {
    this.rightClickListeners.add(fn);
    return () => this.rightClickListeners.delete(fn);
  }

  /** Subscribe to "click on an entity sprite" (castle/army) — emits its region. */
  onEntityClick(fn: (info: { kind: 'castle' | 'army'; regionId: number }) => void): () => void {
    this.entityClickListeners.add(fn);
    return () => this.entityClickListeners.delete(fn);
  }

  /**
   * Highlight a set of regions with a coloured pulsing outline — used by the
   * game layer to indicate "valid move/attack targets" once an army is
   * selected. The colour usually matches the moving army's kingdom.
   * Passing an empty array clears the overlay.
   */
  setActionHighlights(regionIds: number[], color: number = 0xff8a3c): void {
    if (!this.map || !this.actionHighlightLines) return;
    const pts: number[] = [];
    const wanted = new Set(regionIds);
    for (const edge of this.map.edges) {
      const a = edge.cellA;
      const b = edge.cellB;
      const ra = a >= 0 ? this.map.cells[a]?.regionId ?? -1 : -1;
      const rb = b >= 0 ? this.map.cells[b]?.regionId ?? -1 : -1;
      const aIn = ra >= 0 && wanted.has(ra);
      const bIn = rb >= 0 && wanted.has(rb);
      // Outer boundary of the highlighted set
      if (aIn !== bIn) {
        // Sit the outline on top of the higher of the two cells the
        // edge separates — so a highlighted lowland's border draws at
        // the lowland's height (and gets occluded by a neighbouring
        // plateau wall), while a highlighted plateau draws at the
        // plateau's height (visible above the lowland next door).
        const cellA = a >= 0 ? this.map.cells[a] : null;
        const cellB = b >= 0 ? this.map.cells[b] : null;
        const yA = cellA ? cellY(cellA) : 0;
        const yB = cellB ? cellY(cellB) : 0;
        // +0.3 lift to avoid z-fight with the platform face / fill.
        const y = Math.max(yA, yB) + 0.3;
        const pl = edge.polyline;
        for (let i = 0; i < pl.length - 1; i++) {
          const p1 = pl[i]!;
          const p2 = pl[i + 1]!;
          pts.push(p1[0], y, p1[1], p2[0], y, p2[1]);
        }
      }
    }
    const geo = this.actionHighlightLines.geometry;
    geo.dispose();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    geo.computeBoundingSphere();
    // LineDashedMaterial needs cumulative distances pre-computed; without
    // this call dashes don't render. Both lines share geometry topology so
    // we'll reuse the same point list for the black underline.
    this.actionHighlightLines.computeLineDistances();
    // Color + pulse activation
    const mat = this.actionHighlightLines.material as THREE.LineDashedMaterial;
    mat.color.set(color);
    this.actionHighlightActive = pts.length > 0;
    mat.opacity = this.actionHighlightActive ? 1 : 0;

    // Black "shadow" line behind, same geometry, no dashes — visible only
    // in the gaps of the dashed coloured line above it.
    if (this.actionHighlightBlackLines) {
      const blackGeo = this.actionHighlightBlackLines.geometry;
      blackGeo.dispose();
      blackGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
      blackGeo.computeBoundingSphere();
      const blackMat = this.actionHighlightBlackLines.material as THREE.LineBasicMaterial;
      blackMat.opacity = this.actionHighlightActive ? 1 : 0;
    }

    // Rebuild fill mesh: one fan per cell of each highlighted region. The mesh
    // sits at each cell's platform top (respecting elevations) so the colour
    // wash conforms to the terrain instead of floating flat at y=1.5.
    if (this.actionHighlightFill) {
      this.scene.remove(this.actionHighlightFill);
      this.actionHighlightFill.geometry.dispose();
      (this.actionHighlightFill.material as THREE.Material).dispose();
      this.actionHighlightFill = null;
    }
    if (this.actionHighlightActive && this.map) {
      const positions: number[] = [];
      const indices: number[] = [];
      let vOff = 0;
      for (const cell of this.map.cells) {
        if (cell.isOcean) continue;
        if (!wanted.has(cell.regionId)) continue;
        const poly = cell.displacedPolygon;
        if (poly.length < 3) continue;
        const y = cellY(cell) + 0.25; // tiny lift above the platform face
        // Fan from cell centre
        const centerIdx = vOff;
        positions.push(cell.site[0], y, cell.site[1]);
        vOff++;
        for (const p of poly) {
          positions.push(p[0], y, p[1]);
          vOff++;
        }
        const N = poly.length;
        for (let i = 0; i < N; i++) {
          const a = centerIdx + 1 + i;
          const b = centerIdx + 1 + ((i + 1) % N);
          indices.push(centerIdx, a, b);
        }
      }
      const fillGeo = new THREE.BufferGeometry();
      fillGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      fillGeo.setIndex(indices);
      // ── Fill material design ──────────────────────────────────────
      // White overlay using ADDITIVE blending — the wash *adds* light
      // on top of whatever colour the terrain has. The big win is
      // contrast against any background:
      //   • Dark green/brown terrain → noticeably brighter highlight.
      //   • Light beige sand / pale neutral land → pushed almost to
      //     pure white, still clearly visible (the previous straight-
      //     alpha blend made the wash invisible against light tiles
      //     because (1,1,1)*α over a near-white background lands at…
      //     near-white, no contrast).
      //
      // `depthTest: true` is the key bit: without it the wash bled
      // through higher terrain that should occlude it (a region
      // behind a tall hill would still show its highlight as if the
      // hill weren't there). With depth-test on, the platform sides
      // and any closer terrain hide the wash exactly like they hide
      // their own backside.
      //
      // `depthWrite: false` keeps the wash from blocking transparent
      // sprites drawn afterwards. `renderOrder: 95` keeps it under
      // the entity sprites (≥100) so armies/castles/camps draw on
      // top instead of being painted over.
      const fillMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.5,
        depthTest: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
      this.actionHighlightFill = new THREE.Mesh(fillGeo, fillMat);
      this.actionHighlightFill.renderOrder = 95;
      this.scene.add(this.actionHighlightFill);
    }

    // ── Outer-perimeter ribbon (kingdom-colour high-visibility border) ──
    // Single thick band hugging only the OUTER edge of the highlighted
    // set — never along internal borders between two highlighted
    // territories. Built by extruding each perimeter edge segment into
    // a horizontal quad of fixed world-space width (so the line stays
    // chunky regardless of camera distance, unlike a 1-px LineSegments
    // which would render as a hairline on most platforms).
    if (this.actionHighlightOuterBorder) {
      this.scene.remove(this.actionHighlightOuterBorder);
      this.actionHighlightOuterBorder.geometry.dispose();
      (this.actionHighlightOuterBorder.material as THREE.Material).dispose();
      this.actionHighlightOuterBorder = null;
    }
    if (this.actionHighlightActive && this.map && pts.length > 0) {
      const HALF_WIDTH = 1.2; // world units → ~2.4-wide ribbon
      const ribbonPos: number[] = [];
      const ribbonIdx: number[] = [];
      let off = 0;
      // `pts` is a flat [x,y,z, x,y,z, ...] of pairs of points (LineSegments
      // format). Walk it in steps of 6 to get one segment at a time.
      for (let i = 0; i + 5 < pts.length; i += 6) {
        const x1 = pts[i]!,     y1 = pts[i + 1]!, z1 = pts[i + 2]!;
        const x2 = pts[i + 3]!, y2 = pts[i + 4]!, z2 = pts[i + 5]!;
        const dx = x2 - x1;
        const dz = z2 - z1;
        const len = Math.hypot(dx, dz);
        if (len < 1e-4) continue;
        // Unit perpendicular in the XZ plane (line lives flat at its Y).
        const px = -dz / len;
        const pz = dx / len;
        const hx = px * HALF_WIDTH;
        const hz = pz * HALF_WIDTH;
        // 4 vertices of the quad (corners L1/R1/R2/L2).
        ribbonPos.push(x1 - hx, y1, z1 - hz);
        ribbonPos.push(x1 + hx, y1, z1 + hz);
        ribbonPos.push(x2 + hx, y2, z2 + hz);
        ribbonPos.push(x2 - hx, y2, z2 - hz);
        ribbonIdx.push(off, off + 1, off + 2, off, off + 2, off + 3);
        off += 4;
      }
      const ribbonGeo = new THREE.BufferGeometry();
      ribbonGeo.setAttribute('position', new THREE.Float32BufferAttribute(ribbonPos, 3));
      ribbonGeo.setIndex(ribbonIdx);
      const ribbonMat = new THREE.MeshBasicMaterial({
        color, // kingdom banner / castle-shot red — same param as the dashed line
        transparent: true,
        opacity: 0.95,
        depthTest: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.actionHighlightOuterBorder = new THREE.Mesh(ribbonGeo, ribbonMat);
      // Above the wash (95) + the dashed underline (96/97), still under
      // entity sprites (≥100) so it doesn't paint over armies/castles.
      this.actionHighlightOuterBorder.renderOrder = 98;
      this.scene.add(this.actionHighlightOuterBorder);
    }
  }

  /**
   * Smoothly slide the camera target (the point the orbit camera looks at)
   * toward `(x, z)` over `durationMs`. Used to refocus on the active player's
   * castle when a turn starts, or on an action when others play. The camera
   * itself stays at the same offset/angle — only the target moves.
   */
  tweenCameraTo(x: number, z: number, durationMs: number = 600): void {
    if (!this.controls || !this.camera) return;
    const t0 = this.controls.target;
    this.cameraTween = {
      fromX: t0.x,
      fromZ: t0.z,
      toX: x,
      toZ: z,
      startTime: this.clock.elapsedTime,
      duration: durationMs / 1000,
    };
  }

  /**
   * Clamp `controls.target` so the visible viewport always covers the
   * map and never reveals the black void around it. Computes the half-
   * width / half-height of the projected viewport (accounting for zoom),
   * then snaps the target so the viewport bounds stay inside the map.
   *
   * When the viewport is wider than the map dimension (heavy zoom-out
   * was bypassed somehow), we pin the target to the map centre for
   * that axis so the player sees the whole map centred.
   */
  private clampCameraTarget(): void {
    if (!this.controls || !this.camera || !this.map) return;
    const cam = this.camera;
    const mapW = this.map.width;
    const mapH = this.map.height;
    // Allow panning past the map edges by ~25 % of the map dimension. The
    // player still sees the ocean (which extends 3× the map), just at the
    // periphery of the playable area. Beyond this margin the clamp kicks in.
    const marginX = mapW * 0.25;
    const marginZ = mapH * 0.25;
    const minX = -marginX;
    const maxX = mapW + marginX;
    const minZ = -marginZ;
    const maxZ = mapH + marginZ;
    const visibleW = (cam.right - cam.left) / cam.zoom;
    const visibleH = (cam.top - cam.bottom) / cam.zoom;
    const halfVx = visibleW / 2;
    const halfVz = visibleH / 2;
    let tx = this.controls.target.x;
    let tz = this.controls.target.z;
    // X-axis clamp: the viewport's left edge can't go past minX, and its
    // right edge can't go past maxX. If the view is wider than the
    // [min, max] range, pin to the centre.
    const rangeX = maxX - minX;
    if (halfVx * 2 >= rangeX) tx = (minX + maxX) / 2;
    else tx = Math.max(minX + halfVx, Math.min(maxX - halfVx, tx));
    const rangeZ = maxZ - minZ;
    if (halfVz * 2 >= rangeZ) tz = (minZ + maxZ) / 2;
    else tz = Math.max(minZ + halfVz, Math.min(maxZ - halfVz, tz));
    if (tx !== this.controls.target.x || tz !== this.controls.target.z) {
      const dx = tx - this.controls.target.x;
      const dz = tz - this.controls.target.z;
      this.controls.target.x = tx;
      this.controls.target.z = tz;
      this.camera.position.x += dx;
      this.camera.position.z += dz;
    }
  }

  /** Per-frame tween of `controls.target`. Cheap when no animation. */
  private updateCameraTween(): void {
    if (!this.cameraTween || !this.controls || !this.camera) return;
    const tw = this.cameraTween;
    const elapsed = this.clock.elapsedTime - tw.startTime;
    const u = Math.min(1, elapsed / tw.duration);
    const eased = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    const x = tw.fromX + (tw.toX - tw.fromX) * eased;
    const z = tw.fromZ + (tw.toZ - tw.fromZ) * eased;
    const dx = x - this.controls.target.x;
    const dz = z - this.controls.target.z;
    // Slide the camera too so the look angle is preserved.
    this.camera.position.x += dx;
    this.camera.position.z += dz;
    this.controls.target.set(x, 0, z);
    if (u >= 1) this.cameraTween = null;
  }

  /**
   * Mask any entity sitting at `regionId` for `ghostFlightMs`, then fade it
   * back in. Use this right before spawning a ghost that lands at this
   * region so the player doesn't see the destination's "real" entity AND
   * the incoming ghost at the same time (the duplicate-army artefact).
   *
   * If no entity exists there yet (state hasn't applied), the hide is
   * queued and consumed by `setMapEntities` when the entity appears.
   */
  hideArrivingEntity(regionId: number, ghostFlightMs: number): void {
    const until = this.clock.elapsedTime + ghostFlightMs / 1000;
    this.pendingHides.set(regionId, { until });
    // Hide ONLY the army at this region, NOT the castle. Otherwise a
    // friendly castle on the destination would also vanish during the
    // 700 ms ghost flight and pop back in afterwards, which looks broken.
    for (const inst of this.entities.values()) {
      if (inst.regionId === regionId && inst.kind === 'army') {
        this.applyHideToInstance(inst, until);
      }
    }
    // Sweep stale entries so the map doesn't grow forever.
    const now = this.clock.elapsedTime;
    for (const [k, v] of this.pendingHides) {
      if (v.until < now - 5) this.pendingHides.delete(k);
    }
  }

  /**
   * Hide the entity NOW (opacity = 0) and schedule a 250 ms fade-IN to 1
   * starting at `until`. Disappearance is instant so the destination slot
   * is unambiguously empty during the ghost flight — we never want the
   * destination entity overlapping the incoming ghost. Reappearance is
   * smoothed so the new (post-move) sprite doesn't pop in harshly.
   */
  private applyHideToInstance(inst: EntityInstance, until: number): void {
    inst.opacityFrom = 0;
    inst.opacityTarget = 1;
    inst.opacityStartTime = until;
    inst.opacityDuration = 0.25;
    (inst.sprite.material as THREE.SpriteMaterial).opacity = 0;
    for (const s of inst.satellites) {
      (s.material as THREE.SpriteMaterial).opacity = 0;
    }
  }

  /**
   * Reconcile the set of game entities on the map. Diff-based:
   *   - entries already in the registry are MUTATED in place (texture / position
   *     / scale / tint refreshed), preserving wobble phase and any tween;
   *   - entries that vanished from the new list are removed and disposed;
   *   - entries that are new get a fresh sprite.
   *
   * This is what makes movement animations possible — between two patches,
   * the same sprite object persists in the scene and can be tweened.
   */
  async setMapEntities(entities: MapEntity[]): Promise<void> {
    // Pre-load any URL-backed textures (cached → cheap on repeated calls).
    const uniqueUrls = [
      ...new Set(
        entities
          .map((e) => e.spriteUrl)
          .filter((u): u is string => typeof u === 'string'),
      ),
    ];
    await Promise.all(uniqueUrls.map((u) => this.loadEntityTexture(u)));

    const seenIds = new Set<string>();
    for (const ent of entities) {
      seenIds.add(ent.id);
      let tex: THREE.Texture | undefined;
      if (ent.texture) tex = ent.texture;
      else if (ent.spriteUrl) tex = this.spriteCache.get(ent.spriteUrl);
      if (!tex) continue;

      const existing = this.entities.get(ent.id);
      if (existing) {
        // ----- UPDATE in place -----
        const mat = existing.sprite.material as THREE.SpriteMaterial;
        if (mat.map !== tex) mat.map = tex;
        const scale = ent.scale ?? 30;
        const aspect = ent.aspect ?? 1;
        existing.baseScaleX = scale * aspect;
        existing.baseScaleY = scale;
        // Snap to new resting position (animation overlays add deltas on top).
        existing.baseX = ent.position[0];
        existing.baseZ = ent.position[1];
        existing.baseY = (ent.yOffset ?? 0) + 1;
        existing.kind = ent.kind ?? existing.kind;
        existing.regionId = ent.regionId ?? existing.regionId;
        existing.wobble = !!ent.wobble;
        existing.sprite.renderOrder = ent.renderOrder ?? existing.sprite.renderOrder;
        // Position will be re-applied by the animation loop (wobble or rest).
        existing.sprite.position.set(existing.baseX, existing.baseY, existing.baseZ);
        existing.sprite.scale.set(existing.baseScaleX, existing.baseScaleY, 1);
        // Re-attach metadata so the click router stays consistent.
        existing.sprite.userData = {
          kind: existing.kind,
          regionId: existing.regionId,
          wobble: existing.wobble,
        };
        // Tint: schedule a smooth transition if the target differs from what's
        // currently displayed. The updateTint() loop will lerp each frame.
        this.scheduleTintTransition(existing, ent.tint ?? 0xffffff);
        this.syncSatellites(existing, ent);
        // Pending hide consumption — only for ARMIES (a friendly castle
        // on the destination must not be hidden during ghost flight).
        const phU = ent.regionId !== undefined ? this.pendingHides.get(ent.regionId) : undefined;
        if (phU && phU.until > this.clock.elapsedTime && existing.kind === 'army') {
          this.applyHideToInstance(existing, phU.until);
          this.pendingHides.delete(ent.regionId!);
        }
      } else {
        // ----- CREATE -----
        const mat = new THREE.SpriteMaterial({
          map: tex,
          alphaTest: 0.5,
          transparent: true,
          depthTest: false,
          depthWrite: false,
        });
        // New entities ALWAYS start bright. If the target tint is dim, the
        // animation loop will fade it in once the ghost has settled.
        mat.color.setHex(0xffffff);
        const sp = new THREE.Sprite(mat);
        sp.center.set(0.5, 0);
        const baseY = (ent.yOffset ?? 0) + 1;
        sp.position.set(ent.position[0], baseY, ent.position[1]);
        sp.renderOrder = ent.renderOrder ?? 100;
        const scale = ent.scale ?? 30;
        const aspect = ent.aspect ?? 1;
        sp.scale.set(scale * aspect, scale, 1);
        sp.userData = {
          kind: ent.kind,
          regionId: ent.regionId,
          wobble: !!ent.wobble,
        };
        this.scene.add(sp);
        const inst: EntityInstance = {
          sprite: sp,
          kind: ent.kind ?? 'army',
          regionId: ent.regionId ?? -1,
          baseX: ent.position[0],
          baseY,
          baseZ: ent.position[1],
          baseScaleX: scale * aspect,
          baseScaleY: scale,
          wobble: !!ent.wobble,
          phase: Math.random() * Math.PI * 2,
          satellites: [],
          tintCurrent: 0xffffff,
          tintTarget: 0xffffff,
          tintStartTime: 0,
          tintDuration: 0,
          opacityFrom: 1,
          opacityTarget: 1,
          opacityStartTime: 0,
          opacityDuration: 0,
          knockStart: 0,
          knockDuration: 0,
          knockDirX: 0,
          knockDirZ: 0,
        };
        this.entities.set(ent.id, inst);
        this.scheduleTintTransition(inst, ent.tint ?? 0xffffff);
        this.syncSatellites(inst, ent);
        // If a hide was queued for this region before the entity existed,
        // apply it now — but ONLY to armies. A castle on the destination
        // must stay visible during the ghost flight.
        const ph = ent.regionId !== undefined ? this.pendingHides.get(ent.regionId) : undefined;
        if (ph && ph.until > this.clock.elapsedTime && inst.kind === 'army') {
          this.applyHideToInstance(inst, ph.until);
          this.pendingHides.delete(ent.regionId!);
        }
      }
    }

    // ----- REMOVE entries that disappeared -----
    for (const [id, inst] of this.entities) {
      if (seenIds.has(id)) continue;
      this.scene.remove(inst.sprite);
      inst.sprite.material.dispose();
      for (const s of inst.satellites) {
        this.scene.remove(s);
        (s.material as THREE.SpriteMaterial).dispose();
      }
      this.entities.delete(id);
    }
  }

  /**
   * Reconcile satellite sprites for an entity. Each satellite is a half-size
   * billboard glued to the main entity. ONLY texture, scale, tint and the
   * relative offset (stored in userData) are managed here — the actual
   * world position is recomputed each frame in `updateWobble` so satellites
   * follow the parent's animated position (wobble, future tweens, etc.).
   */
  /**
   * Schedule a smooth tint transition for an entity. The transition style
   * depends on the direction:
   *   bright → dim : 700ms hold (lets the move ghost land) + 400ms fade
   *   dim → bright : 0ms hold + 250ms fade (snappy re-activation)
   *   other        : snap to target immediately
   */
  private scheduleTintTransition(inst: EntityInstance, target: number): void {
    if (target === inst.tintTarget) return; // already heading there
    const bright = 0xffffff;
    const dim = 0x4c4c4c;
    const t = this.clock.elapsedTime;
    if (target === dim && inst.tintCurrent === bright) {
      inst.tintTarget = target;
      inst.tintStartTime = t + 0.7; // wait for ghost flight
      inst.tintDuration = 0.4;
    } else if (target === bright && inst.tintCurrent === dim) {
      inst.tintTarget = target;
      inst.tintStartTime = t;
      inst.tintDuration = 0.25;
    } else {
      // Snap (covers fresh enemy entities + same-color cases).
      inst.tintCurrent = target;
      inst.tintTarget = target;
      inst.tintStartTime = 0;
      inst.tintDuration = 0;
      (inst.sprite.material as THREE.SpriteMaterial).color.setHex(target);
      for (const s of inst.satellites) {
        (s.material as THREE.SpriteMaterial).color.setHex(target);
      }
    }
  }

  /** Per-frame tint + opacity lerp. Cheap unless something's animating. */
  private updateTint(): void {
    if (this.entities.size === 0) return;
    const t = this.clock.elapsedTime;
    for (const inst of this.entities.values()) {
      // ── Tint ───────────────────────────────────────────────────────────
      if (inst.tintCurrent !== inst.tintTarget && t >= inst.tintStartTime) {
        const elapsed = t - inst.tintStartTime;
        const u = inst.tintDuration > 0 ? Math.min(1, elapsed / inst.tintDuration) : 1;
        // Ease-in-out cubic so the transition feels intentional, not linear.
        const eased = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
        const lerped = lerpHexColor(inst.tintCurrent, inst.tintTarget, eased);
        (inst.sprite.material as THREE.SpriteMaterial).color.setHex(lerped);
        for (const s of inst.satellites) {
          (s.material as THREE.SpriteMaterial).color.setHex(lerped);
        }
        if (u >= 1) inst.tintCurrent = inst.tintTarget;
      }
      // ── Opacity (hide+fade-in) ─────────────────────────────────────────
      if (inst.opacityFrom !== inst.opacityTarget) {
        let o: number;
        if (t < inst.opacityStartTime) {
          o = inst.opacityFrom; // still holding the pre-transition value
        } else {
          const u = inst.opacityDuration > 0
            ? Math.min(1, (t - inst.opacityStartTime) / inst.opacityDuration)
            : 1;
          o = inst.opacityFrom + (inst.opacityTarget - inst.opacityFrom) * u;
          if (u >= 1) inst.opacityFrom = inst.opacityTarget;
        }
        (inst.sprite.material as THREE.SpriteMaterial).opacity = o;
        for (const s of inst.satellites) {
          (s.material as THREE.SpriteMaterial).opacity = o;
        }
      }
    }
  }

  private syncSatellites(inst: EntityInstance, ent: MapEntity): void {
    const want = ent.satellites ?? [];
    // Remove excess
    while (inst.satellites.length > want.length) {
      const s = inst.satellites.pop()!;
      this.scene.remove(s);
      (s.material as THREE.SpriteMaterial).dispose();
    }
    // Update or create
    const mainTint = ent.tint ?? 0xffffff;
    const baseScale = (ent.scale ?? 30) * 0.5;
    for (let i = 0; i < want.length; i++) {
      const w = want[i]!;
      const aspect = w.aspect ?? 1;
      let s = inst.satellites[i];
      const offset = { x: w.offset[0], z: w.offset[1] };
      if (!s) {
        const mat = new THREE.SpriteMaterial({
          map: w.texture,
          alphaTest: 0.5,
          transparent: true,
          depthTest: false,
          depthWrite: false,
        });
        s = new THREE.Sprite(mat);
        s.center.set(0.5, 0);
        // Draw above the main sprite (slightly higher renderOrder).
        s.renderOrder = (ent.renderOrder ?? 100) + 1;
        // Click + offset tags so we can route satellite clicks to the parent
        // entity's region AND position the satellite during the wobble loop.
        s.userData = {
          kind: ent.kind,
          regionId: ent.regionId,
          wobble: false,
          satellite: true,
          satOffset: offset,
        };
        this.scene.add(s);
        inst.satellites.push(s);
      } else {
        const mat = s.material as THREE.SpriteMaterial;
        if (mat.map !== w.texture) mat.map = w.texture;
        s.renderOrder = (ent.renderOrder ?? 100) + 1;
        s.userData = {
          kind: ent.kind,
          regionId: ent.regionId,
          wobble: false,
          satellite: true,
          satOffset: offset,
        };
      }
      const mat = s.material as THREE.SpriteMaterial;
      mat.color.setHex(mainTint);
      s.scale.set(baseScale * aspect, baseScale, 1);
      // Initial placement so the satellite is "near" the parent before the
      // first wobble tick. Wobble loop will override this every frame.
      s.position.set(inst.baseX + offset.x, inst.baseY + 0.5, inst.baseZ + offset.z);
    }
  }

  /**
   * Spawn a transient "ghost" sprite that flies from one map position to
   * another. Used for movement animations: when an army moves from A to B
   * we update the registry immediately (sprite snaps to B), AND spawn a
   * ghost from A to B that arcs over the terrain. The visual reads as
   * "something flew over from there".
   */
  async spawnGhost(opts: {
    texture?: THREE.Texture;
    spriteUrl?: string;
    fromPosition: [number, number];
    toPosition: [number, number];
    scale: number;
    aspect?: number;
    tint?: number;
    /** Animation length in ms (default 600). */
    duration?: number;
    /** Peak Y above the platform during flight (default 18). */
    hopHeight?: number;
    /** Anchor Y offset (default 8 — slightly higher than entities). */
    yOffset?: number;
    /** Optional faction colour for the "footstep dust" trail emitted
     *  under the ghost as it travels. Omit for no trail. */
    trailColor?: number;
  }): Promise<void> {
    let tex: THREE.Texture | undefined;
    if (opts.texture) tex = opts.texture;
    else if (opts.spriteUrl) tex = await this.loadEntityTexture(opts.spriteUrl);
    if (!tex) return;
    const mat = new THREE.SpriteMaterial({
      map: tex,
      alphaTest: 0.5,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    if (opts.tint !== undefined) mat.color.setHex(opts.tint);
    const sp = new THREE.Sprite(mat);
    sp.center.set(0.5, 0);
    const aspect = opts.aspect ?? 1;
    sp.scale.set(opts.scale * aspect, opts.scale, 1);
    sp.renderOrder = 250; // above entities
    const y = (opts.yOffset ?? 8) + 1;
    sp.position.set(opts.fromPosition[0], y, opts.fromPosition[1]);
    this.scene.add(sp);
    return new Promise<void>((resolve) => {
      this.ghosts.push({
        sprite: sp,
        startTime: this.clock.elapsedTime,
        duration: (opts.duration ?? 600) / 1000,
        fromX: opts.fromPosition[0],
        fromZ: opts.fromPosition[1],
        toX: opts.toPosition[0],
        toZ: opts.toPosition[1],
        y,
        hopHeight: opts.hopHeight ?? 18,
        onComplete: () => resolve(),
        trailColor: opts.trailColor,
        lastTrailEmit: 0,
      });
    });
  }

  /**
   * Spawn a burst of pixel-art confetti at a world position. The particles
   * fly outward, lifted by an initial upward velocity, then pulled down by
   * gravity — a classic "impact" effect. Used at the destination of a
   * combat resolution; `color` should be the relevant faction's banner so
   * the player can read at a glance "who got hit / who hit".
   */
  /**
   * Spawn a flat expanding ring on the water surface — used for snacalia
   * takeoff/landing splashes so the player perceives a 3D water disturbance,
   * not just a 2D particle pop. Mesh is a `RingGeometry` laid flat on the XZ
   * plane at the given Y (slightly above OCEAN_FLOOR_Y to avoid Z-fighting).
   */
  private spawnRippleRing(
    at: [number, number],
    opts: { y?: number; startRadius?: number; endRadius?: number; duration?: number; color?: number } = {},
  ): void {
    const y = opts.y ?? OCEAN_FLOOR_Y + 0.15;
    const startRadius = opts.startRadius ?? 1.5;
    const endRadius = opts.endRadius ?? 12;
    const duration = opts.duration ?? 0.65;
    const color = opts.color ?? 0xffffff;
    // Unit-radius ring: scale via Mesh.scale each frame so we don't churn geometry.
    const geo = new THREE.RingGeometry(0.92, 1.0, 28, 1);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2; // lay flat on the water
    mesh.position.set(at[0], y, at[1]);
    mesh.scale.set(startRadius, startRadius, 1);
    mesh.renderOrder = 65; // above ocean, below sprites
    this.scene.add(mesh);
    this.rippleRings.push({
      mesh,
      startTime: this.clock.elapsedTime,
      duration,
      startRadius,
      endRadius,
    });
  }

  spawnImpactBurst(
    at: [number, number],
    color: number,
    opts: { count?: number; baseY?: number; spread?: number } = {},
  ): void {
    if (!this.whiteSquareTex) return;
    const count = opts.count ?? 16;
    const baseY = opts.baseY ?? 6;
    const spread = opts.spread ?? 1;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (20 + Math.random() * 30) * spread;
      const vx = Math.cos(angle) * speed;
      const vz = Math.sin(angle) * speed;
      const vy = 30 + Math.random() * 35;
      const lifetime = 0.55 + Math.random() * 0.45;
      const size = 1.8 + Math.random() * 2.4;

      const mat = new THREE.SpriteMaterial({
        map: this.whiteSquareTex,
        color,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      const sp = new THREE.Sprite(mat);
      sp.position.set(at[0], baseY, at[1]);
      sp.scale.set(size, size, 1);
      sp.renderOrder = 300;
      this.scene.add(sp);
      this.particles.push({
        sprite: sp,
        startTime: this.clock.elapsedTime,
        duration: lifetime,
        startPos: [at[0], baseY, at[1]],
        velocity: [vx, vy, vz],
        gravity: -160, // pulls confetti back down — keeps the burst compact
        startScale: size,
        endScale: size * 0.4, // shrink as they fade
      });
    }
  }

  /**
   * Death FX for a defeated army. Two synchronized sprites:
   *   1. A "squash" copy of the army composite — flattens vertically while
   *      fading out. Reads as "the army collapses on the ground."
   *   2. A pixel-art ghost icon (iconchat_mort_fantome.png) that rises
   *      toward the sky while swaying left-right and fading out.
   * Pass the army's composite texture so the squashed body matches the
   * defeated faction's sprite.
   */
  async spawnDeathFx(
    at: [number, number],
    bodyTexture: THREE.Texture,
    opts: {
      bodyScale?: number;
      bodyAspect?: number;
      delayMs?: number;
    } = {},
  ): Promise<void> {
    const bodyScale = opts.bodyScale ?? 45;
    const aspect = opts.bodyAspect ?? 1;
    const baseY = 4;

    // Schedule the FX. We use setTimeout for the delay so the renderer's
    // clock doesn't have to track it — the squashed body and the ghost
    // both pop into being together after the delay.
    const start = (): void => {
      // ── 1. Squashed body ──────────────────────────────────────────────
      const bodyMat = new THREE.SpriteMaterial({
        map: bodyTexture,
        alphaTest: 0.3,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      const bodySprite = new THREE.Sprite(bodyMat);
      bodySprite.center.set(0.5, 0);
      bodySprite.scale.set(bodyScale * aspect, bodyScale, 1);
      bodySprite.position.set(at[0], baseY, at[1]);
      // BEHIND map entities. Castle / army sprites render at 100+, so a
      // squashed corpse at renderOrder 80 sits visually behind the
      // attacker that just arrived on the same region. The player sees
      // the attacker plant their flag, with the squashed defender body
      // tucked underneath — matches the "victor on top" reading.
      bodySprite.renderOrder = 80;
      this.scene.add(bodySprite);
      this.particles.push({
        sprite: bodySprite,
        startTime: this.clock.elapsedTime,
        duration: 0.7,
        startPos: [at[0], baseY, at[1]],
        velocity: [0, 0, 0],
        gravity: 0,
        startScale: bodyScale,
        endScale: bodyScale, // unused (squashOnly)
        squashOnly: true,
        aspect,
      });

      // ── 2. Rising ghost iconchat ──────────────────────────────────────
      void this.loadEntityTexture('/sprites/iconchat_mort_fantome.png').then((ghostTex) => {
        const ghostMat = new THREE.SpriteMaterial({
          map: ghostTex,
          alphaTest: 0.3,
          transparent: true,
          depthTest: false,
          depthWrite: false,
        });
        const ghostSprite = new THREE.Sprite(ghostMat);
        ghostSprite.center.set(0.5, 0);
        const ghostScale = 24;
        ghostSprite.scale.set(ghostScale, ghostScale, 1);
        ghostSprite.position.set(at[0], baseY + 8, at[1]);
        ghostSprite.renderOrder = 310;
        this.scene.add(ghostSprite);
        this.particles.push({
          sprite: ghostSprite,
          startTime: this.clock.elapsedTime,
          duration: 1.8,
          startPos: [at[0], baseY + 8, at[1]],
          // Rises ~70 world units over the lifetime (linear).
          velocity: [0, 70 / 1.8, 0],
          gravity: 0,
          startScale: ghostScale,
          endScale: ghostScale * 0.9, // slight shrink as it ascends
          // Sways left-right ~12 world units with one full cycle per second.
          swayAmplitude: 12,
          swayFreq: 1,
          aspect: 1,
        });
      });
    };
    if (opts.delayMs && opts.delayMs > 0) setTimeout(start, opts.delayMs);
    else start();
  }

  /**
   * Drop a "Pattée spéciale" sprite from the sky onto a region. The sprite
   * falls with gravity, lands at the centroid, then bobs gently in place
   * until the caller removes it via `clearDrop()`. Returns the sprite so
   * the call site can keep a reference for later removal.
   */
  spawnDropFromSky(
    at: [number, number],
    textureUrl: string,
    opts: { scale?: number } = {},
  ): THREE.Sprite | null {
    const scale = opts.scale ?? 28;
    const tex = this.spriteCache.get(textureUrl);
    // We try to load eagerly so the sprite exists immediately. If the texture
    // isn't loaded yet, kick off the load and bail.
    if (!tex) {
      void this.loadEntityTexture(textureUrl);
      return null;
    }
    const mat = new THREE.SpriteMaterial({
      map: tex,
      alphaTest: 0.3,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sp = new THREE.Sprite(mat);
    sp.center.set(0.5, 0);
    sp.scale.set(scale, scale, 1);
    sp.renderOrder = 220;
    // Start high in the sky so the player sees it fall.
    const restingY = 8;
    sp.position.set(at[0], 250, at[1]);
    this.scene.add(sp);
    this.drops.push({
      sprite: sp,
      x: at[0], z: at[1],
      restingY,
      startTime: this.clock.elapsedTime,
      landedAt: 0,
    });
    return sp;
  }

  /** Remove the drop sprite for `regionId` (call when collected/expired). */
  clearDrop(sprite: THREE.Sprite | null): void {
    if (!sprite) return;
    const idx = this.drops.findIndex((d) => d.sprite === sprite);
    if (idx < 0) return;
    this.drops.splice(idx, 1);
    this.scene.remove(sprite);
    (sprite.material as THREE.SpriteMaterial).dispose();
  }

  /**
   * Spawn a "barbarian invasion" boat that sails from the open ocean
   * to a coastal landing region. The boat travels in a straight line
   * but the starting point is chosen so the entire path stays in the
   * ocean — never crossing another kingdom's territory.
   *
   * How the path is built:
   *   1. Walk every cell of the landing region; for each cell, collect
   *      its OCEAN neighbours.
   *   2. Average those ocean-neighbour centres → the "outward" point.
   *   3. Direction = (outward − centroid) normalised → that's the
   *      vector pointing AWAY from the land, into open water.
   *   4. Step outward from the coast in that direction, sampling at
   *      8-wu intervals up to ~160 wu. Each sample is validated with
   *      `isOceanPoint()`; the boat's starting position is the last
   *      sample that was still in open ocean. This guarantees a clean
   *      straight line over water with no land-crossing.
   *
   * Resources used:
   *   - `/sprites/barbare_ship.png` — the boat sprite. The bow points
   *     right in the source image; we flip on scale.x when the boat
   *     is sailing leftward so it always faces the direction of
   *     travel. If the texture isn't loaded yet the call is retried
   *     after the load resolves.
   */
  spawnBarbarianBoat(
    landingRegionId: number,
    opts: {
      duration?: number;
      scale?: number;
      followCamera?: boolean;
      onDone?: () => void;
    } = {},
  ): void {
    if (!this.map) return;
    const region = this.map.regions[landingRegionId];
    if (!region) return;
    const url = '/sprites/barbare_ship.png';
    const tex = this.spriteCache.get(url);
    if (!tex) {
      void this.loadEntityTexture(url).then(() => {
        this.spawnBarbarianBoat(landingRegionId, opts);
      });
      return;
    }
    // Compute outward direction (region centroid → average ocean neighbour cell).
    let oxSum = 0, ozSum = 0, n = 0;
    for (const cellIdx of region.cells) {
      const cell = this.map.cells[cellIdx];
      if (!cell) continue;
      for (const ni of cell.neighbors) {
        const nc = this.map.cells[ni];
        if (!nc || !nc.isOcean) continue;
        oxSum += nc.site[0];
        ozSum += nc.site[1];
        n += 1;
      }
    }
    if (n === 0) return; // not actually coastal — bail rather than spawn on land
    const cx = region.centroid[0];
    const cz = region.centroid[1];
    let dx = oxSum / n - cx;
    let dz = ozSum / n - cz;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.01) return;
    dx /= len; dz /= len;
    // Find the FARTHEST ocean point in the outward direction. The
    // previous algorithm walked outward from the centroid and broke at
    // the first non-ocean step — which fired immediately on regions
    // wider than 8 wu, leaving the boat spawning right on top of the
    // landing tile.
    //
    // New approach: search from FAR (~200 wu) inward in 6-wu decrements
    // and keep the first ocean hit. That picks the deepest open-water
    // point on a straight line out from the coast. Also tries a few
    // angular variants (±18°) so a region with awkward geometry still
    // finds a clean approach.
    let startX = cx, startZ = cz;
    let foundDistance = 0;
    const angleOffsets = [0, -0.31, 0.31]; // 0, ±18°
    for (const off of angleOffsets) {
      const cos = Math.cos(off);
      const sin = Math.sin(off);
      const rx = dx * cos - dz * sin;
      const rz = dx * sin + dz * cos;
      for (let d = 220; d >= 30; d -= 6) {
        const sx = cx + rx * d;
        const sz = cz + rz * d;
        if (this.isOceanPoint(sx, sz, 0)) {
          if (d > foundDistance) {
            foundDistance = d;
            startX = sx; startZ = sz;
          }
          break; // next angle
        }
      }
    }
    // Defensive fallback: if NO ocean point was found (very narrow
    // map?) walk outward in tiny steps until we hit ocean, then push
    // a bit further. Worst case we spawn ~30 wu from the coast.
    if (foundDistance === 0) {
      for (let d = 6; d <= 120; d += 4) {
        const sx = cx + dx * d;
        const sz = cz + dz * d;
        if (this.isOceanPoint(sx, sz, 0)) {
          startX = sx; startZ = sz;
          foundDistance = d;
        }
      }
      if (foundDistance === 0) {
        startX = cx + dx * 30;
        startZ = cz + dz * 30;
      }
    }
    // Build the boat sprite anchored at the ocean floor with a slight
    // bob applied per frame in updateBoats(). The sprite's bow points
    // right in the source PNG, so we mirror horizontally (negative
    // scale.x) when the boat is sailing right-to-left in world space.
    // Without this the boat would visually reverse into shore on any
    // landing west of the spawn point.
    const mat = new THREE.SpriteMaterial({
      map: tex,
      alphaTest: 0.3,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sp = new THREE.Sprite(mat);
    sp.center.set(0.5, 0.5);
    const scale = opts.scale ?? 64;
    // travelDx > 0 → sailing rightward (no flip). travelDx < 0 → flip.
    // We compare ON SCREEN-X axis because the orthographic camera is
    // tilted 45° around X — sprite.scale.x maps to screen-horizontal
    // regardless of camera angle.
    const travelDx = cx - startX;
    const faceX = travelDx >= 0 ? scale : -scale;
    sp.scale.set(faceX, scale, 1);
    sp.renderOrder = 220;
    sp.position.set(startX, OCEAN_FLOOR_Y + 1.5, startZ);
    this.scene.add(sp);
    const duration = (opts.duration ?? 6000) / 1000;
    this.boats.push({
      sprite: sp,
      fromX: startX, fromZ: startZ,
      toX: cx, toZ: cz,
      startTime: this.clock.elapsedTime,
      duration,
      followCamera: opts.followCamera ?? false,
      onDone: opts.onDone,
      doneFired: false,
    });
  }

  /** Per-frame interpolation of every active boat. Removes finished
   *  boats from the scene + the active list. */
  private updateBoats(): void {
    if (this.boats.length === 0) return;
    const t = this.clock.elapsedTime;
    for (let i = this.boats.length - 1; i >= 0; i--) {
      const b = this.boats[i]!;
      const elapsed = t - b.startTime;
      const u = Math.min(1, elapsed / b.duration);
      // Ease-out quadratic — the boat decelerates as it approaches
      // the coast, like a real landing manoeuvre.
      const eased = 1 - Math.pow(1 - u, 2);
      b.sprite.position.x = b.fromX + (b.toX - b.fromX) * eased;
      b.sprite.position.z = b.fromZ + (b.toZ - b.fromZ) * eased;
      // Wave bob — bigger amplitude than a Pattée drop so the boat
      // visibly rides choppy water across its long sail-in.
      b.sprite.position.y = OCEAN_FLOOR_Y + 1.5 + Math.sin(t * 3.5 + i) * 1.2;
      // Sea-swell rocking — a gentle ±7° rotation on a slightly
      // different period from the y-bob so the two motions don't
      // sync up (more natural / less mechanical). Damped near the
      // very end (u > 0.92) so the boat doesn't wobble while
      // beaching itself.
      const dampen = u > 0.92 ? Math.max(0, (1 - u) / 0.08) : 1;
      const rock = Math.sin(t * 2.3 + i * 0.7) * 0.12 * dampen;
      (b.sprite.material as THREE.SpriteMaterial).rotation = rock;
      // Camera follow: lock controls.target to the boat's XZ position
      // each frame so the player sees the journey, not just the
      // landing tile. Cancel any other camera tween while a follow
      // boat is in flight (otherwise the two would fight each other).
      if (b.followCamera && this.controls && this.camera) {
        this.cameraTween = null;
        const tx = b.sprite.position.x;
        const tz = b.sprite.position.z;
        const dx = tx - this.controls.target.x;
        const dz = tz - this.controls.target.z;
        this.controls.target.set(tx, 0, tz);
        // Slide the camera the same delta to keep the look angle.
        this.camera.position.x += dx;
        this.camera.position.z += dz;
      }
      // ── Wake: emit a foam puff behind the boat at a fixed cadence ──
      // The puff spawns slightly behind the hull (opposite the travel
      // direction), then expands + fades via the particle system. Only
      // while the boat is actually moving (u < 0.98) so it stops
      // frothing once beached.
      if (this.foamTex && u < 0.98) {
        const last = this.lastWakeAt[i] ?? 0;
        if (t - last > 0.14) {
          this.lastWakeAt[i] = t;
          // Travel direction (normalised) → wake sits behind it.
          const tdx = b.toX - b.fromX;
          const tdz = b.toZ - b.fromZ;
          const len = Math.hypot(tdx, tdz) || 1;
          const backX = b.sprite.position.x - (tdx / len) * 14;
          const backZ = b.sprite.position.z - (tdz / len) * 14;
          for (let k = 0; k < 2; k++) {
            const jitter = (Math.random() - 0.5) * 8;
            const px = backX + (-(tdz / len)) * jitter;
            const pz = backZ + (tdx / len) * jitter;
            const mat = new THREE.SpriteMaterial({
              map: this.foamTex,
              transparent: true,
              depthTest: false,
              depthWrite: false,
              opacity: 0.85,
            });
            const sp = new THREE.Sprite(mat);
            sp.position.set(px, OCEAN_FLOOR_Y + 0.6, pz);
            sp.scale.set(6, 6, 1);
            sp.renderOrder = 210;
            this.scene.add(sp);
            this.particles.push({
              sprite: sp,
              startTime: t,
              duration: 1.1,
              startPos: [px, OCEAN_FLOOR_Y + 0.6, pz],
              velocity: [0, 0, 0],
              gravity: 0,
              startScale: 6,
              endScale: 16, // spread out into froth as it dissipates
            });
          }
        }
      }
      if (u >= 1) {
        if (!b.doneFired) {
          b.doneFired = true;
          try { b.onDone?.(); } catch { /* swallow */ }
        }
        this.scene.remove(b.sprite);
        (b.sprite.material as THREE.SpriteMaterial).dispose();
        this.boats.splice(i, 1);
        this.lastWakeAt.splice(i, 1);
      }
    }
  }

  /**
   * Spawn a "capture wave" — a coloured flood that sweeps across a freshly
   * conquered region from the conquering edge (the source region's side),
   * revealing the new owner's colour as it goes. Sits on a thin overlay
   * mesh just above the region's land cells.
   *
   * @param regionId   the captured region
   * @param newColor   the conqueror's banner colour (0xRRGGBB)
   * @param origin     world XZ the wave radiates FROM (the source region's
   *                   centroid). When undefined (e.g. no adjacent source —
   *                   a neutralised tile), the wave radiates from the
   *                   region's own centroid so it still animates cleanly.
   */
  spawnCaptureWave(
    regionId: number,
    newColor: number,
    origin: [number, number] | undefined,
    durationMs = 750,
  ): void {
    if (!this.map) return;
    const region = this.map.regions[regionId];
    if (!region) return;
    // Build an overlay geometry from the region's land cells (fan-
    // triangulated, same as the terrain). Carries world XZ per vertex so
    // the shader can measure distance from the wave origin.
    const positions: number[] = [];
    const worldXZ: number[] = [];
    const indices: number[] = [];
    let vOff = 0;
    const cx = region.centroid[0];
    const cz = region.centroid[1];
    for (const cellIdx of region.cells) {
      const cell = this.map.cells[cellIdx];
      if (!cell || cell.isOcean) continue;
      const poly = cell.displacedPolygon;
      if (poly.length < 3) continue;
      const y = cellY(cell) + 0.35; // hover just above the platform top
      const centerIdx = vOff;
      positions.push(cell.site[0], y, cell.site[1]);
      worldXZ.push(cell.site[0], cell.site[1]);
      vOff++;
      const rimStart = vOff;
      for (const p of poly) {
        positions.push(p[0], y, p[1]);
        worldXZ.push(p[0], p[1]);
      }
      vOff += poly.length;
      for (let i = 0; i < poly.length; i++) {
        const next = (i + 1) % poly.length;
        indices.push(centerIdx, rimStart + i, rimStart + next);
      }
    }
    if (positions.length === 0) return;
    const ox = origin ? origin[0] : cx;
    const oz = origin ? origin[1] : cz;
    // Furthest vertex distance from the origin → normalises the sweep so
    // it always finishes covering the whole region at progress = 1.
    let maxDist = 1;
    for (let i = 0; i < worldXZ.length; i += 2) {
      const dx = worldXZ[i]! - ox;
      const dz = worldXZ[i + 1]! - oz;
      const d = Math.hypot(dx, dz);
      if (d > maxDist) maxDist = d;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('aWorldXZ', new THREE.Float32BufferAttribute(worldXZ, 2));
    geo.setIndex(indices);
    const col = new THREE.Color().setHex(newColor, THREE.SRGBColorSpace);
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uOrigin: { value: new THREE.Vector2(ox, oz) },
        uColor: { value: new THREE.Vector3(col.r, col.g, col.b) },
        uProgress: { value: 0 },
        uMaxDist: { value: maxDist },
        uOpacity: { value: 0.92 },
      },
      vertexShader: `
        attribute vec2 aWorldXZ;
        varying vec2 vXZ;
        void main() {
          vXZ = aWorldXZ;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec2 uOrigin;
        uniform vec3 uColor;
        uniform float uProgress;
        uniform float uMaxDist;
        uniform float uOpacity;
        varying vec2 vXZ;
        void main() {
          float d = distance(vXZ, uOrigin);
          float front = uProgress * (uMaxDist + 24.0);
          // Fully covered behind the wavefront; a bright leading band at
          // the front edge; nothing ahead of it.
          float covered = step(d, front);
          float band = smoothstep(front, front - 22.0, d); // 1 at front, 0 deep inside
          vec3 c = mix(uColor, vec3(1.0), band * 0.6);     // whiten the crest
          float a = covered * uOpacity;
          if (a <= 0.001) discard;
          gl_FragColor = vec4(c, a);
        }
      `,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = 60; // above terrain (which is ~0), below entities (≥88)
    this.scene.add(mesh);
    this.captureWaves.push({
      mesh,
      material: mat,
      startTime: this.clock.elapsedTime,
      duration: durationMs / 1000,
      fadeStart: durationMs / 1000, // start fading once the sweep completes
    });
  }

  /** Per-frame: advance every capture wave's progress, then fade + remove. */
  private updateCaptureWaves(): void {
    if (this.captureWaves.length === 0) return;
    const t = this.clock.elapsedTime;
    const FADE = 0.45;
    for (let i = this.captureWaves.length - 1; i >= 0; i--) {
      const w = this.captureWaves[i]!;
      const elapsed = t - w.startTime;
      const u = Math.min(1, elapsed / w.duration);
      // Ease-out so the wave decelerates as it fills the tile.
      w.material.uniforms.uProgress!.value = 1 - Math.pow(1 - u, 2);
      if (elapsed > w.fadeStart) {
        const f = Math.min(1, (elapsed - w.fadeStart) / FADE);
        w.material.uniforms.uOpacity!.value = 0.92 * (1 - f);
        if (f >= 1) {
          this.scene.remove(w.mesh);
          w.mesh.geometry.dispose();
          w.material.dispose();
          this.captureWaves.splice(i, 1);
        }
      }
    }
  }

  /**
   * Drop a paw-print stamp on a conquered tile — a cat-themed mark that
   * scales up from small and fades over ~900 ms, tinted to the
   * conqueror's colour. Pure cosmetic flourish layered on the capture.
   */
  spawnPawStamp(at: [number, number], color: number, y = 5): void {
    if (!this.pawTex) return;
    const mat = new THREE.SpriteMaterial({
      map: this.pawTex,
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      opacity: 0.95,
      rotation: (Math.random() - 0.5) * 0.6, // slight random tilt
    });
    const sp = new THREE.Sprite(mat);
    sp.position.set(at[0], y, at[1]);
    const baseScale = 22;
    sp.scale.set(baseScale * 0.4, baseScale * 0.4, 1);
    sp.renderOrder = 250;
    this.scene.add(sp);
    this.pawStamps.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration: 0.9,
      baseScale,
    });
  }

  /** Per-frame: pop-in scale + fade-out for paw stamps. */
  private updatePawStamps(): void {
    if (this.pawStamps.length === 0) return;
    const t = this.clock.elapsedTime;
    for (let i = this.pawStamps.length - 1; i >= 0; i--) {
      const s = this.pawStamps[i]!;
      const u = Math.min(1, (t - s.startTime) / s.duration);
      // Scale: overshoot to 1.1× by 40 %, settle to 1× — a satisfying stamp.
      const eased = u < 0.4
        ? (u / 0.4) * 1.1
        : 1.1 - ((u - 0.4) / 0.6) * 0.1;
      const sc = s.baseScale * (0.4 + 0.6 * Math.min(1, eased));
      s.sprite.scale.set(sc, sc, 1);
      // Hold opaque for the first half, then fade out.
      const mat = s.sprite.material as THREE.SpriteMaterial;
      mat.opacity = u < 0.5 ? 0.95 : 0.95 * (1 - (u - 0.5) / 0.5);
      if (u >= 1) {
        this.scene.remove(s.sprite);
        mat.dispose();
        this.pawStamps.splice(i, 1);
      }
    }
  }

  /**
   * Reconcile the set of waving faction flags planted on castles. Called
   * from the game layer (applyEntities) with one descriptor per castle
   * region. Adds new flags, removes flags for regions that lost their
   * castle, and updates the texture/colour when a castle changes hands.
   *
   * Each descriptor: { regionId, position [x,z], factionId, castleScale }.
   * The flag is sized small relative to the castle (≈ 38 % of its height)
   * and planted at the castle's top-right so it reads as a banner without
   * dwarfing the keep.
   */
  setCastleFlags(
    flags: Array<{ regionId: number; factionId: string; castleScale: number }>,
  ): void {
    if (!this.map) return;
    const wanted = new Set(flags.map((f) => f.regionId));
    // Remove flags whose castle is gone.
    for (const [regionId, f] of this.castleFlags) {
      if (!wanted.has(regionId)) {
        this.scene.remove(f.sprite);
        (f.sprite.material as THREE.SpriteMaterial).dispose();
        this.castleFlags.delete(regionId);
      }
    }
    for (const desc of flags) {
      const region = this.map.regions[desc.regionId];
      if (!region) continue;
      const url = `/sprites/${desc.factionId}_flag.png`;
      const tex = this.spriteCache.get(url);
      if (!tex) {
        // Texture not loaded yet — kick the loader; the next applyEntities
        // pass (state patch) will pick it up. Avoids a flag popping in
        // half-built.
        void this.loadEntityTexture(url);
        continue;
      }
      // Platform height at the castle's tile (so the flag sits on top of
      // the keep, not floating at sea level on a tall mountain region).
      let platformY = 4;
      for (const ci of region.cells) {
        const cell = this.map.cells[ci];
        if (cell && !cell.isOcean) { platformY = cellY(cell); break; }
      }
      // Flag size: ~38 % of the castle height, capped so it never looks
      // huge. Flags are ~square-ish; keep aspect ~0.85.
      const flagH = Math.min(26, desc.castleScale * 0.38);
      const flagW = flagH * 0.85;
      const baseX = region.centroid[0] + desc.castleScale * 0.22; // toward the right
      const baseZ = region.centroid[1];
      const baseY = platformY + desc.castleScale * 0.55;          // up near the top
      const existing = this.castleFlags.get(desc.regionId);
      if (existing) {
        // Update texture (faction may have changed on capture).
        const mat = existing.sprite.material as THREE.SpriteMaterial;
        if (mat.map !== tex) { mat.map = tex; mat.needsUpdate = true; }
        existing.baseX = baseX;
        existing.baseY = baseY;
        existing.baseZ = baseZ;
        existing.sprite.scale.set(flagW, flagH, 1);
        continue;
      }
      const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      const sp = new THREE.Sprite(mat);
      sp.center.set(0.5, 0); // anchor at the pole's base
      sp.position.set(baseX, baseY, baseZ);
      sp.scale.set(flagW, flagH, 1);
      sp.renderOrder = 95; // above the castle sprite (90)
      this.scene.add(sp);
      this.castleFlags.set(desc.regionId, {
        sprite: sp,
        baseX, baseY, baseZ,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  /** Per-frame: gentle "wind" wave on every castle flag — a small skew
   *  rotation + horizontal bob, each flag on its own phase. */
  private updateCastleFlags(): void {
    if (this.castleFlags.size === 0) return;
    const t = this.clock.elapsedTime;
    for (const f of this.castleFlags.values()) {
      // Flutter: rotate ±0.09 rad and bob X slightly, different phase each.
      const flutter = Math.sin(t * 3 + f.phase);
      (f.sprite.material as THREE.SpriteMaterial).rotation = flutter * 0.09;
      f.sprite.position.x = f.baseX + flutter * 0.7;
      f.sprite.position.y = f.baseY + Math.sin(t * 2 + f.phase) * 0.4;
    }
  }

  /**
   * Spawn a single fast-flying square (a "projectile") from one position to
   * another. Used for archer / crossbow shots — each arrow is one call.
   * Travels in a straight line over the terrain (no arc, no gravity).
   */
  /**
   * Heavy cannonball: a single big projectile that follows a parabolic arc
   * from castle to target, leaves a trail of smoke puffs along the way, and
   * triggers an extra-large impact burst on landing. Looks distinct from
   * the small fast arrow volley used by archer attacks.
   */
  spawnCannonball(
    from: [number, number],
    to: [number, number],
    color: number,
    opts: { duration?: number; size?: number; startY?: number; arcHeight?: number } = {},
  ): void {
    if (!this.whiteSquareTex) return;
    const durationMs = opts.duration ?? 850;
    const T = durationMs / 1000;
    const size = opts.size ?? 10;
    const startY = opts.startY ?? 16;
    const arcHeight = opts.arcHeight ?? 36;
    // For y(t) = y0 + vy·t + ½·g·t² to start at y0, peak at y0+arcHeight at
    // t = T/2, and return to y0 at t = T:
    //   vy = 4·h / T
    //   g  = -8·h / T²
    const vy = (4 * arcHeight) / T;
    const g = -(8 * arcHeight) / (T * T);
    const vx = (to[0] - from[0]) / T;
    const vz = (to[1] - from[1]) / T;

    // The main cannonball — dark grey/black with a colored tint for the
    // shooter's banner so the player tracks who shot. Larger sprite for
    // weight, no fade until the end via the existing particle system.
    const mat = new THREE.SpriteMaterial({
      map: this.whiteSquareTex,
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sp = new THREE.Sprite(mat);
    sp.position.set(from[0], startY, from[1]);
    sp.scale.set(size, size, 1);
    sp.renderOrder = 290;
    this.scene.add(sp);
    this.particles.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration: T,
      startPos: [from[0], startY, from[1]],
      velocity: [vx, vy, vz],
      gravity: g,
      startScale: size,
      endScale: size * 0.9,
    });

    // Smoke puffs along the trajectory: schedule N puffs at uniform time
    // intervals so the trail follows the arc. Each puff drifts upward and
    // grows + fades over ~0.6 s. Spawn time is offset; spawn position is
    // computed from the current ballistic equation.
    const NUM_SMOKE = 8;
    for (let i = 0; i < NUM_SMOKE; i++) {
      const tFrac = i / (NUM_SMOKE - 1);
      window.setTimeout(() => {
        const tt = tFrac * T;
        const x = from[0] + vx * tt;
        const y = startY + vy * tt + 0.5 * g * tt * tt;
        const z = from[1] + vz * tt;
        const puffMat = new THREE.SpriteMaterial({
          map: this.whiteSquareTex,
          color: 0x555555,
          transparent: true,
          opacity: 0.6,
          depthTest: false,
          depthWrite: false,
        });
        const puff = new THREE.Sprite(puffMat);
        puff.position.set(x, y, z);
        puff.scale.set(5, 5, 1);
        puff.renderOrder = 285;
        this.scene.add(puff);
        this.particles.push({
          sprite: puff,
          startTime: this.clock.elapsedTime,
          duration: 0.55,
          startPos: [x, y, z],
          velocity: [0, 4, 0], // slow rise
          gravity: 0,
          startScale: 5,
          endScale: 11, // expands as it dissipates
        });
      }, tFrac * durationMs);
    }

    // Extra-large impact burst at the landing point.
    window.setTimeout(() => {
      this.spawnImpactBurst(to, color, { count: 28, spread: 1.6, baseY: 4 });
      // A second softer burst in white for a flash effect.
      this.spawnImpactBurst(to, 0xffffff, { count: 12, spread: 0.7, baseY: 6 });
    }, durationMs);
  }

  spawnProjectile(
    from: [number, number],
    to: [number, number],
    color: number,
    opts: {
      duration?: number;
      size?: number;
      y?: number;
      /** Optional landing Y. If omitted, the arrow returns to start Y
       *  (parabolic arc — archer shooting between ground-level cells).
       *  Different `endY` lets the projectile drop from one altitude to
       *  another, e.g. arrow rain falling from sky to terrain. */
      endY?: number;
      arcHeight?: number;
    } = {},
  ): void {
    // Sized for visibility against the terrain. Previous defaults (size 10,
    // arcHeight 16) made the arrow read as a tiny dot and the small arc
    // could clip behind taller cell platforms (hills/mountains sit at y 14
    // and up). Bumping size + start-Y + arc keeps it clearly above the map.
    const durationMs = opts.duration ?? 650;
    const duration = durationMs / 1000;
    const y = opts.y ?? 22;
    const endY = opts.endY ?? y; // default: return to start (arc behaviour)
    const size = opts.size ?? 29; // +60 % vs 18 — visible from a glance
    const arcHeight = opts.arcHeight ?? 32;
    // Prefer the dedicated arrow sprite. Falls back to a white square the
    // very first call if the texture hasn't loaded yet — but we kick off
    // the load so subsequent arrows use the real sprite.
    let tex: THREE.Texture | undefined = this.spriteCache.get('/sprites/projectile_fleche.png');
    if (!tex) {
      void this.loadEntityTexture('/sprites/projectile_fleche.png');
      tex = this.whiteSquareTex ?? undefined;
    }
    if (!tex) return;
    const usingArrow = tex !== this.whiteSquareTex;

    // The arrow image has its tip at top-centre, so at sprite rotation = 0
    // the arrow points UP in screen space. We rotate around the sprite's
    // view axis so the tip aligns with the XZ flight direction.
    // SpriteMaterial.rotation is CCW positive in screen space; rotating
    // by `-atan2(dz, dx) - π/2` aligns "up" with the (dx, dz) motion vector.
    const dx = to[0] - from[0];
    const dz = to[1] - from[1];
    const rotation = usingArrow
      ? -Math.atan2(dz, dx) - Math.PI / 2
      : 0;

    const mat = new THREE.SpriteMaterial({
      map: tex,
      // Always tint with the shooter's faction colour, including when
      // we're using the real arrow art — multiplying the texture by
      // the banner colour makes friend/foe instantly readable in a
      // 4-8 player free-for-all. (Previously the arrow texture was
      // forced to white, which left the projectile invisible against
      // light terrain — the player only saw the impact burst.)
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    mat.rotation = rotation;
    const sp = new THREE.Sprite(mat);
    sp.position.set(from[0], y, from[1]);
    sp.scale.set(size, size, 1);
    sp.renderOrder = 290;
    this.scene.add(sp);

    // Trajectory equations:
    //   y(t) = y0 + vy·t + ½·g·t²
    // We want y(0) = y, y(T) = endY, and the peak to sit `arcHeight` above
    // the max of (y, endY). For a same-Y arc this reduces to the classic
    // vy = 4·h/T, g = -8·h/T². For a falling arrow (endY < y, arcHeight = 0)
    // we drop linearly. The two contributions compose without surprise.
    const T = duration;
    const dyLinear = endY - y;
    // Symmetric arc component (peak above both endpoints).
    const vyArc = (4 * arcHeight) / T;
    const gArc = -(8 * arcHeight) / (T * T);
    // Linear vertical drift to satisfy y(T) = endY on top of the arc.
    const vyLin = dyLinear / T;
    const vy = vyArc + vyLin;
    const g = gArc;
    const vx = dx / T;
    const vz = dz / T;
    this.particles.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration: T,
      startPos: [from[0], y, from[1]],
      velocity: [vx, vy, vz],
      gravity: g,
      startScale: size,
      endScale: size,
      // Keep the arrow oriented along its flight direction every frame.
      fixedRotation: rotation,
    });
  }

  /**
   * Rain of arrows falling from the sky onto `target`. Used by castle
   * shots — replaces the heavy cannonball with a volley that better
   * matches the player's intuition of "siege archers raining shafts on
   * the enemy". Arrows are staggered for a true rain feel and scattered
   * around the target for visual variety.
   */
  spawnArrowRain(
    target: [number, number],
    color: number,
    opts: { count?: number; spread?: number; stagger?: number } = {},
  ): void {
    const count = opts.count ?? 14;
    const spread = opts.spread ?? 22;
    const stagger = opts.stagger ?? 55;
    // Arrows drop near-vertically from a high Y onto the target with a
    // small horizontal offset (more natural than perfectly vertical —
    // gives each arrow a clear flight direction so the sprite rotation
    // logic in `spawnProjectile` doesn't pick a random orientation).
    const HIGH_Y = 90;
    const GROUND_Y = 14;
    for (let i = 0; i < count; i++) {
      const delay = i * stagger;
      window.setTimeout(() => {
        // Landing point: jittered within `spread` of the target.
        const landX = target[0] + (Math.random() - 0.5) * spread * 2;
        const landZ = target[1] + (Math.random() - 0.5) * spread * 2;
        // Launch point: slightly NW of the landing point so the arrow's
        // flight vector is unambiguous (atan2 is stable).
        const fromX = landX - 8 - Math.random() * 12;
        const fromZ = landZ - 8 - Math.random() * 12;
        this.spawnProjectile(
          [fromX, fromZ],
          [landX, landZ],
          color,
          {
            duration: 480 + Math.random() * 140,
            size: 26 + Math.random() * 6,
            y: HIGH_Y,
            endY: GROUND_Y,
            arcHeight: 0, // straight diving fall, no peak
          },
        );
        // Small impact burst per arrow — sand/dust effect when each
        // shaft hits the ground.
        window.setTimeout(() => {
          this.spawnImpactBurst([landX, landZ], color, { count: 6, spread: 0.5, baseY: GROUND_Y + 1 });
        }, 480);
      }, delay);
    }
  }

  /** Cached async loader for one-off entity textures (castles, armies). */
  private async loadEntityTexture(url: string): Promise<THREE.Texture> {
    let tex = this.spriteCache.get(url);
    if (tex) return tex;
    const loader = new THREE.TextureLoader();
    tex = await loader.loadAsync(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    this.spriteCache.set(url, tex);
    return tex;
  }

  setMap(map: MapData): void {
    this.map = map;
    this.disposeScene();
    this.buildScene();
    this.placeGrass();
    this.setupCamera();
    this.setupControls();
  }

  fitToScreen(): void {
    this.handleResize();
  }

  // --------------------------------------------------------------------------

  private startLoop(): void {
    const loop = (): void => {
      requestAnimationFrame(loop);
      const dt = this.clock.getDelta();
      this.controls?.update();
      this.clampCameraTarget();
      // Drive the animated ocean shader.
      const oUniform = this.oceanMaterial?.uniforms.uTime;
      if (oUniform) oUniform.value = this.clock.elapsedTime;
      this.updateSnacalias(dt);
      this.updateRippleRings();
      this.updateGrass(dt);
      this.updateLeaves(dt);
      // Order matters: tint first, THEN wobble. The wobble step can
      // override the tint during a knockback flash; without this order
      // the natural tint would always win.
      this.updateTint();
      this.updateWobble();
      this.updateGhosts();
      this.updateClashes();
      this.updateParticles();
      this.updateDrops();
      this.updateBoats();
      this.updateCaptureWaves();
      this.updatePawStamps();
      this.updateCastleFlags();
      this.updateActionHighlightPulse();
      this.updateCameraTween();
      if (this.camera) this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  /**
   * Idle "impatient" sway+squash on armies. Each sprite picks its own random
   * phase at creation so the field doesn't pulse in sync. We oscillate scale
   * X and Y in anti-phase (volume-preserving squash) and add a tiny X drift.
   */
  /** Pulse both the outline and the translucent fill at ~5 Hz. */
  private updateActionHighlightPulse(): void {
    if (!this.actionHighlightActive) return;
    const t = this.clock.elapsedTime;
    const phase = 0.5 + 0.5 * Math.sin(t * 10);
    if (this.actionHighlightLines) {
      const mat = this.actionHighlightLines.material as THREE.LineDashedMaterial;
      mat.opacity = 0.5 + 0.5 * phase;
    }
    if (this.actionHighlightBlackLines) {
      const mat = this.actionHighlightBlackLines.material as THREE.LineBasicMaterial;
      mat.opacity = 0.5 + 0.5 * phase;
    }
    if (this.actionHighlightFill) {
      const mat = this.actionHighlightFill.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.18 + 0.32 * phase; // a softer wash than the outline
    }
  }

  /**
   * Trigger a one-shot "got hit by arrows" animation on any army
   * sitting on `regionId`. Pushes the sprite away from `(fromX, fromZ)`
   * (the shooter's tile) for ~120 ms, then eases back, with a brief
   * tint flash. Used by the ranged attack flow when defenders SURVIVE
   * the volley — otherwise the player only saw a confetti burst with
   * no feedback on the army itself.
   *
   * Direction is auto-normalised; if from === to we just shake in place.
   */
  knockEntity(
    regionId: number,
    fromX: number,
    fromZ: number,
    opts: { strength?: number; duration?: number } = {},
  ): void {
    let inst: EntityInstance | undefined;
    for (const e of this.entities.values()) {
      if (e.kind === 'army' && e.regionId === regionId) { inst = e; break; }
    }
    if (!inst) return;
    const dx = inst.baseX - fromX;
    const dz = inst.baseZ - fromZ;
    const len = Math.hypot(dx, dz);
    const strength = opts.strength ?? 5; // world-unit peak offset
    if (len < 0.01) {
      // Shooter on top of target → just shake horizontally.
      inst.knockDirX = (Math.random() - 0.5) * strength;
      inst.knockDirZ = (Math.random() - 0.5) * strength;
    } else {
      inst.knockDirX = (dx / len) * strength;
      inst.knockDirZ = (dz / len) * strength;
    }
    inst.knockStart = this.clock.elapsedTime;
    inst.knockDuration = opts.duration ?? 0.42; // 420 ms total reaction
  }

  private updateWobble(): void {
    if (this.entities.size === 0) return;
    const t = this.clock.elapsedTime;
    for (const inst of this.entities.values()) {
      // ── Hit-reaction knockback (one-shot, decays in knockDuration) ──
      // Computed BEFORE the wobble so the offsets compose nicely on top
      // of the resting position. Profile:
      //   • 0–25 % of duration : ease-out push to peak offset
      //   • 25–100 %           : ease-in return to zero
      //   • tint flash : white at peak, fades back to current tint
      let knockX = 0;
      let knockZ = 0;
      let knockScaleBoost = 0;
      let knockTintMix = 0;
      if (inst.knockStart > 0) {
        const k = (t - inst.knockStart) / inst.knockDuration;
        if (k >= 1) {
          inst.knockStart = 0;     // done — reset
        } else {
          // Triangle envelope for the offset.
          const env = k < 0.25
            ? (k / 0.25)              // 0 → 1 (push out)
            : (1 - (k - 0.25) / 0.75); // 1 → 0 (return)
          knockX = inst.knockDirX * env;
          knockZ = inst.knockDirZ * env;
          // Scale punch — sprite stutters bigger at the peak then
          // shrinks back. Cheap "ouf" visual on top of the offset.
          knockScaleBoost = env * 0.12;
          // Tint flash — full white in the first 30 % of the knock,
          // fades back to natural tint by 100 %.
          knockTintMix = Math.max(0, 1 - k / 0.30);
        }
      }

      // Parent wobble first (so satellites can read the freshly-computed
      // position.x below).
      if (inst.wobble) {
        const w = Math.sin(t * 3 + inst.phase);
        inst.sprite.scale.x = inst.baseScaleX * (1 + 0.06 * w + knockScaleBoost);
        inst.sprite.scale.y = inst.baseScaleY * (1 - 0.06 * w + knockScaleBoost);
        inst.sprite.position.x = inst.baseX + Math.sin(t * 1.7 + inst.phase) * 1.2 + knockX;
      } else if (inst.knockStart > 0) {
        // Non-wobbling entity (castle / camp) — still apply the knock.
        inst.sprite.scale.x = inst.baseScaleX * (1 + knockScaleBoost);
        inst.sprite.scale.y = inst.baseScaleY * (1 + knockScaleBoost);
        inst.sprite.position.x = inst.baseX + knockX;
      }
      // Knockback Z lives on the sprite directly (sprites don't normally
      // animate Z; we restore via baseZ when the knock ends).
      inst.sprite.position.z = inst.baseZ + knockZ;
      // Tint flash — applied on top of the existing tint state so the
      // updateTint() lerp continues to run underneath. We lerp the
      // displayed material color toward white during the flash.
      if (knockTintMix > 0) {
        const mat = inst.sprite.material as THREE.SpriteMaterial;
        // Pull the current colour halfway toward white at the peak.
        const target = new THREE.Color(0xffffff);
        mat.color.lerp(target, knockTintMix * 0.7);
      }
      // Satellites: anchored to the parent's CURRENT animated position +
      // their stored offset, with their own small bobble + wobble so the
      // mini-cluster feels organic. They share the parent's tint and follow
      // it on moves (registry update calls syncSatellites which writes
      // `baseX/Y/Z` indirectly via inst).
      if (inst.satellites.length > 0) {
        const parentX = inst.sprite.position.x;
        // Parent y is computed below if wobble was applied; otherwise baseY.
        // We use baseY for satellites as their reference height — the parent
        // doesn't actually animate Y during wobble, only scale.
        for (let si = 0; si < inst.satellites.length; si++) {
          const s = inst.satellites[si]!;
          const off = (s.userData as { satOffset?: { x: number; z: number } }).satOffset
            ?? { x: 0, z: 0 };
          // Per-satellite wobble: same kind as the parent but with a per-index
          // phase offset, so the mini-cluster doesn't pulse in lockstep.
          const satPhase = inst.phase + si * 1.3;
          const swayX = Math.sin(t * 1.7 + satPhase) * 1.0;
          const bobY = Math.sin(t * 2.2 + satPhase) * 0.8;
          const squash = Math.sin(t * 3 + satPhase);
          s.position.x = parentX + off.x + swayX;
          s.position.z = inst.baseZ + off.z;
          s.position.y = inst.baseY + 0.5 + bobY;
          // Half-size wobble scaling (anti-phase X/Y for volume preservation).
          const baseW = inst.baseScaleX * 0.5;
          const baseH = inst.baseScaleY * 0.5;
          s.scale.x = baseW * (1 + 0.06 * squash);
          s.scale.y = baseH * (1 - 0.06 * squash);
        }
      }
      // Skip the rest if no wobble (parent already updated above).
      if (!inst.wobble) continue;
    }
  }

  /**
   * Advance every particle. Position = startPos + v·t + ½·g·t² (only Y feels
   * gravity). Scale lerps from startScale to endScale. Opacity fades in the
   * last 30% of lifetime. Done particles are removed.
   */
  private updateParticles(): void {
    if (this.particles.length === 0) return;
    const now = this.clock.elapsedTime;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      const t = now - p.startTime;
      const u = Math.min(1, t / p.duration);
      // Optional X-sway — used for the ghost rising from a defeated army.
      const sway = p.swayAmplitude && p.swayFreq
        ? Math.sin(t * p.swayFreq * Math.PI * 2) * p.swayAmplitude
        : 0;
      p.sprite.position.set(
        p.startPos[0] + p.velocity[0] * t + sway,
        p.startPos[1] + p.velocity[1] * t + 0.5 * p.gravity * t * t,
        p.startPos[2] + p.velocity[2] * t,
      );
      // Scale: by default, isotropic lerp. For squash-only (death sprite),
      // X stays at the start width and Y flattens to 0.
      if (p.squashOnly) {
        const aspect = p.aspect ?? 1;
        p.sprite.scale.x = p.startScale * aspect;
        p.sprite.scale.y = p.startScale * (1 - u);
      } else {
        const s = p.startScale + (p.endScale - p.startScale) * u;
        const aspect = p.aspect ?? 1;
        p.sprite.scale.set(s * aspect, s, 1);
      }
      const mat = p.sprite.material as THREE.SpriteMaterial;
      mat.opacity = u < 0.7 ? 1 : 1 - (u - 0.7) / 0.3;
      // Keep aimed projectiles pointing along their flight direction.
      if (p.fixedRotation !== undefined) {
        mat.rotation = p.fixedRotation;
      }
      if (u >= 1) {
        this.scene.remove(p.sprite);
        mat.dispose();
        this.particles.splice(i, 1);
      }
    }
  }

  /**
   * Advance every ghost sprite along its arc. Position lerps linearly between
   * (from, to); Y adds a sine-shaped hop so the sprite arcs over the ground.
   * Ghosts that completed are removed from the scene and the array.
   */
  /**
   * Per-frame update of falling-then-bobbing drops. Two phases:
   *   1. Fall — gravity pulls the sprite from y=250 to its resting height.
   *   2. Bob — gentle vertical sine oscillation forever after landing.
   */
  /**
   * Spawn a snacalia (jumping fish) every 4-8s at a random ocean point in
   * the ring around the map. The animation is a parabolic arc with a small
   * Z-axis rotation to suggest the body tumbling out of and back into the
   * water. Splash particles fire at takeoff + landing.
   */
  private updateSnacalias(_dt: number): void {
    if (!this.snacaliaTexture || !this.map) return;
    const now = this.clock.elapsedTime;

    // Population-capped spawning. We want up to MAX_SNACALIAS fish in
    // the air at the same time so the water always feels alive — but
    // we still throttle the spawn rate so they don't all pop together.
    // Each jump lasts ~1.5-1.9 s; with a 0.6-1.6 s spawn window and a
    // cap of 3, the screen typically shows 2 fish at any moment with
    // brief peaks of 3.
    const MAX_SNACALIAS = 3;
    if (now >= this.nextSnacaliaSpawnAt && this.snacalias.length < MAX_SNACALIAS) {
      this.spawnSnacalia();
      this.nextSnacaliaSpawnAt = now + 0.6 + Math.random();
    }

    // Animate existing instances.
    for (let i = this.snacalias.length - 1; i >= 0; i--) {
      const s = this.snacalias[i]!;
      const u = Math.min(1, (now - s.startTime) / s.duration);
      // Half-sine arc: y starts low, peaks at u=0.5, returns low.
      const baseY = OCEAN_FLOOR_Y;
      const y = baseY + Math.sin(u * Math.PI) * s.apexY;
      const x = s.fromX + (s.toX - s.fromX) * u;
      const z = s.fromZ + (s.toZ - s.fromZ) * u;
      s.sprite.position.set(x, y, z);
      // Rotation (Z-axis screen space for Sprite). Mirrors a fish leaping:
      // tail up at takeoff, level at apex, tail down at landing.
      const rot = (u - 0.5) * 1.05; // -0.525..+0.525 rad ≈ -30°..+30°
      const mat = s.sprite.material as THREE.SpriteMaterial;
      mat.rotation = rot;

      // --- Uniform scale animation (small → big → small) ---
      // The snacalia pops out small, grows to full size in the air, then
      // shrinks back down as it dives — pairs nicely with the fade in/out
      // to sell the "appears from the water / vanishes into the water" feel.
      let uniformScale: number;
      if (u < 0.20) {
        // Grow from 0.3× to 1.0× over the first 20% of the jump.
        uniformScale = 0.30 + (u / 0.20) * 0.70;
      } else if (u > 0.80) {
        // Shrink from 1.0× to 0.3× over the last 20% of the jump.
        uniformScale = 1.00 - ((u - 0.80) / 0.20) * 0.70;
      } else {
        uniformScale = 1.0;
      }

      // --- Squash / stretch / wobble (on top of uniform scale) ---
      // Base wobble: small in-flight body oscillation (squash horizontally
      // while stretching vertically and vice versa).
      const wob = Math.sin((now - s.startTime) * s.wobbleFreq) * 0.08;
      // Entry/exit stretch: when u < 0.12 (just leaving water) or u > 0.88
      // (about to plunge back in) the body is dramatically stretched
      // vertically — like a water column splash following the snacalia.
      let stretchY = 0;
      let squashX = 0;
      if (u < 0.12) {
        const t = 1 - u / 0.12;            // 1 → 0 over the exit window
        stretchY = t * 0.55;
        squashX = t * 0.30;
      } else if (u > 0.88) {
        const t = (u - 0.88) / 0.12;       // 0 → 1 entering the water
        stretchY = t * 0.55;
        squashX = t * 0.30;
      }
      const sx = s.baseScale * uniformScale * (1 + wob - squashX);
      const sy = s.baseScale * uniformScale * (1 - wob + stretchY);
      s.sprite.scale.set(sx, sy, 1);

      // --- Fade in / fade out ---
      // 15 % fade in, 15 % fade out, full opacity in the middle.
      let alpha = 1;
      if (u < 0.15) alpha = u / 0.15;
      else if (u > 0.85) alpha = (1 - u) / 0.15;
      mat.opacity = Math.max(0, Math.min(1, alpha));

      // Splash on takeoff (first ~5 % of lifetime). Bigger burst + a flat
      // ripple ring on the water for a real 3D water-disturbance feel.
      if (!s.splashedTakeoff && u > 0.02 && u < 0.10) {
        s.splashedTakeoff = true;
        this.spawnImpactBurst([s.fromX, s.fromZ], 0xb8d8f0, { count: 14, baseY: baseY + 1.2, spread: 0.9 });
        this.spawnImpactBurst([s.fromX, s.fromZ], 0xffffff, { count: 6, baseY: baseY + 1.6, spread: 0.55 });
        this.spawnRippleRing([s.fromX, s.fromZ], { startRadius: 2, endRadius: 14, duration: 0.7 });
      }
      // Landing: bigger burst + a wider ripple ring (the entry splash reads
      // larger in classic cartoon water sequences).
      if (u >= 1) {
        this.spawnImpactBurst([s.toX, s.toZ], 0xb8d8f0, { count: 18, baseY: baseY + 1.2, spread: 1.0 });
        this.spawnImpactBurst([s.toX, s.toZ], 0xffffff, { count: 8, baseY: baseY + 1.8, spread: 0.6 });
        this.spawnRippleRing([s.toX, s.toZ], { startRadius: 2, endRadius: 18, duration: 0.85 });
        this.scene.remove(s.sprite);
        (s.sprite.material as THREE.SpriteMaterial).dispose();
        this.snacalias.splice(i, 1);
      }
    }
  }

  /**
   * Advance every active ripple ring: lerp its radius from start → end,
   * fade opacity to 0, and despawn when the duration elapses. Cheap to run
   * because most of the time there are zero or one ring on screen.
   */
  private updateRippleRings(): void {
    if (this.rippleRings.length === 0) return;
    const now = this.clock.elapsedTime;
    for (let i = this.rippleRings.length - 1; i >= 0; i--) {
      const r = this.rippleRings[i]!;
      const u = Math.min(1, (now - r.startTime) / r.duration);
      // Ease-out so the ring grows fast at first then slows — looks more
      // like real water expanding.
      const eased = 1 - (1 - u) * (1 - u);
      const radius = r.startRadius + (r.endRadius - r.startRadius) * eased;
      r.mesh.scale.set(radius, radius, 1);
      const mat = r.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 1 - u;
      if (u >= 1) {
        this.scene.remove(r.mesh);
        r.mesh.geometry.dispose();
        mat.dispose();
        this.rippleRings.splice(i, 1);
      }
    }
  }

  /**
   * Point-in-ocean test. Iterates non-ocean cells (constrained by the
   * continent's bounding box first for speed) and returns false if (x, z)
   * lands inside any land polygon. Used to make sure the snacalia takeoff
   * and landing splashes don't end up on a territory — the splash animation
   * is meant to read as "the fish leaves / re-enters the water," so
   * landing on dry land looks broken.
   *
   * `margin` extends each polygon outward (approximated via its bbox + margin)
   * so the splash effect also has a few wu of breathing room around any cliff.
   */
  private isOceanPoint(x: number, z: number, margin: number = 6): boolean {
    if (!this.map) return true;
    // Use continent bboxes for fast pre-rejection.
    for (const cont of this.map.continents) {
      const b = cont.bounds;
      if (
        x < b.minX - margin || x > b.maxX + margin ||
        z < b.minY - margin || z > b.maxY + margin
      ) continue;
      // Within continent bbox; precisely test each non-ocean cell.
      for (const cellIdx of cont.cells) {
        const cell = this.map.cells[cellIdx];
        if (!cell || cell.isOcean) continue;
        // Quick reject by cell bbox (computed inline from polygon).
        const poly = cell.displacedPolygon;
        if (poly.length < 3) continue;
        let minX = poly[0]![0], maxX = poly[0]![0];
        let minZ = poly[0]![1], maxZ = poly[0]![1];
        for (let i = 1; i < poly.length; i++) {
          const px = poly[i]![0], pz = poly[i]![1];
          if (px < minX) minX = px; else if (px > maxX) maxX = px;
          if (pz < minZ) minZ = pz; else if (pz > maxZ) maxZ = pz;
        }
        if (x < minX - margin || x > maxX + margin || z < minZ - margin || z > maxZ + margin) continue;
        // Precise ray-casting point-in-polygon test.
        let inside = false;
        for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
          const xi = poly[i]![0], zi = poly[i]![1];
          const xj = poly[j]![0], zj = poly[j]![1];
          if (((zi > z) !== (zj > z)) &&
              (x < ((xj - xi) * (z - zi)) / (zj - zi) + xi)) {
            inside = !inside;
          }
        }
        if (inside) return false;
      }
    }
    return true;
  }

  /**
   * Pick a random point in the ocean close to the map so the player actually
   * sees the snacalia jump. We sample a radius between 0.30 and 0.50 of the
   * larger map dimension — that keeps fish near (but not inside) continents.
   * Both the takeoff and landing positions are validated against the land
   * polygons; we retry up to 16 times before giving up on this spawn slot.
   */
  private spawnSnacalia(): void {
    if (!this.snacaliaTexture || !this.map) return;
    const cx = this.map.width / 2;
    const cz = this.map.height / 2;

    // Reject-sampling: regenerate source + destination until both sit in
    // open water (with a 6 wu margin away from any land polygon).
    let fx = 0, fz = 0, tx = 0, tz = 0;
    let valid = false;
    for (let attempt = 0; attempt < 16 && !valid; attempt++) {
      const radius = Math.max(this.map.width, this.map.height) * (0.30 + Math.random() * 0.20);
      const angle = Math.random() * Math.PI * 2;
      fx = cx + Math.cos(angle) * radius;
      fz = cz + Math.sin(angle) * radius;
      const dirAngle = angle + Math.PI / 2 + (Math.random() - 0.5) * 0.5;
      const dist = 60 + Math.random() * 50;
      tx = fx + Math.cos(dirAngle) * dist;
      tz = fz + Math.sin(dirAngle) * dist;
      // Both ends must be in ocean — otherwise the splash lands on a
      // continent and the animation looks broken.
      if (this.isOceanPoint(fx, fz) && this.isOceanPoint(tx, tz)) valid = true;
    }
    // If we couldn't find a valid spot in 16 tries the map is essentially
    // all-land; bail rather than spawn a snacalia on a territory.
    if (!valid) return;
    const mat = new THREE.SpriteMaterial({
      map: this.snacaliaTexture,
      transparent: true,
      // alphaTest cuts fade-in/out, drop it so opacity tween works.
      depthTest: false,
      depthWrite: false,
    });
    mat.opacity = 0;              // start invisible, fades in via updater
    const sp = new THREE.Sprite(mat);
    sp.center.set(0.5, 0.5);
    // Bigger snacalia so they're actually visible at typical zoom.
    const baseScale = 60;
    sp.scale.set(baseScale, baseScale, 1);
    sp.position.set(fx, OCEAN_FLOOR_Y, fz);
    sp.renderOrder = 60;          // above ocean, below land entities
    this.scene.add(sp);
    this.snacalias.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration: 1.5 + Math.random() * 0.4,
      fromX: fx, fromZ: fz,
      toX: tx, toZ: tz,
      apexY: 16 + Math.random() * 8,    // higher arc to match bigger sprite
      baseScale,
      wobbleFreq: 18 + Math.random() * 6, // rad/s — snappy fish-body wobble
      splashedTakeoff: false,
    });
  }

  // --------------------------------------------------------------------------
  // Flying leaves — ambient decoration carried by the wind from left to right.
  //
  // Each leaf spawns just off the left edge of the map at a random height,
  // drifts rightward with a gentle sinusoidal bob, slowly rotates as it
  // tumbles, and fades out over its lifetime. Two colour tints alternate
  // (dark green + ocean blue) so the wind feels alive without all the
  // leaves reading as the same particle.

  private updateLeaves(_dt: number): void {
    if (!this.map) return;
    const now = this.clock.elapsedTime;

    // Population-capped spawning. Cap raised to 16 leaves so the wind
    // feels really alive across the whole map (previously 8 was too
    // sparse for the player to consistently notice the effect).
    const MAX_LEAVES = 16;
    if (now >= this.nextLeafSpawnAt && this.leaves.length < MAX_LEAVES) {
      this.spawnLeaf();
      // Tightened spawn gap (0.35-0.9 s) to fill the cap quickly.
      this.nextLeafSpawnAt = now + 0.35 + Math.random() * 0.55;
    }

    // Animate the live ones.
    for (let i = this.leaves.length - 1; i >= 0; i--) {
      const l = this.leaves[i]!;
      const t = now - l.startTime;
      const u = Math.min(1, t / l.duration);
      // Position: linear wind drift + sinusoidal bob on Y.
      const x = l.fromX + l.vx * t;
      const z = l.fromZ + l.vz * t;
      const y = l.baseY + Math.sin(t * l.bobFreq + l.rotPhase) * l.bobAmp;
      l.sprite.position.set(x, y, z);
      // Lazy tumble: rotation accumulates over time.
      const mat = l.sprite.material as THREE.SpriteMaterial;
      mat.rotation = l.rotPhase + l.rotRate * t;
      // Fade out across the lifetime — 0..1 fade-in over first 12 %,
      // hold at full opacity, then long fade-out over the last 35 %.
      let alpha: number;
      if (u < 0.12) alpha = u / 0.12;
      else if (u > 0.65) alpha = 1 - (u - 0.65) / 0.35;
      else alpha = 1;
      mat.opacity = Math.max(0, Math.min(1, alpha));
      if (u >= 1) {
        this.scene.remove(l.sprite);
        mat.dispose();
        this.leaves.splice(i, 1);
      }
    }
  }

  private spawnLeaf(): void {
    if (!this.leafTexture || !this.map) return;

    // ── Pick a random spawn + endpoint both on LAND ─────────────────
    // Leaves are an ambient land effect — never spawn over open
    // water and never let them drift off into the sea (the player
    // notices the discontinuity instantly). Reject-sample a start
    // point on land whose endpoint (along the GLOBAL wind direction)
    // also lands on land. Up to 24 tries before giving up this tick.
    const mapW = this.map.width;
    const mapH = this.map.height;
    // Same wind angle for every leaf — they all drift together,
    // as if a single breeze were sweeping the map. Speed is per-leaf
    // so the swarm isn't a rigid grid: faster leaves overtake slower
    // ones along the same line of flow.
    const angle = this.leafWindAngle;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    let fromX = 0, fromZ = 0;
    let vx = 0, vz = 0;
    let duration = 0;
    let valid = false;
    for (let attempt = 0; attempt < 24 && !valid; attempt++) {
      const sx = Math.random() * mapW;
      const sz = Math.random() * mapH;
      if (this.isOceanPoint(sx, sz)) continue;
      const dist = 60 + Math.random() * 70;
      const speed = 22 + Math.random() * 18;           // 22–40 wu/s — leisurely
      const ex = sx + cosA * dist;
      const ez = sz + sinA * dist;
      if (this.isOceanPoint(ex, ez)) continue;
      fromX = sx;
      fromZ = sz;
      vx = cosA * speed;
      vz = sinA * speed;
      duration = dist / speed;                          // exactly cover the path
      valid = true;
    }
    if (!valid) return;

    // ── Tint: alternate dark green + ocean blue ─────────────────────
    const greenHex = 0x1f5a25;
    const blueHex = 0x2a6f9c;
    const isGreen = this.leafColorToggle;
    this.leafColorToggle = !this.leafColorToggle;
    const tint = isGreen ? greenHex : blueHex;
    const mat = new THREE.SpriteMaterial({
      map: this.leafTexture,
      color: tint,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    mat.opacity = 0; // fades in via updateLeaves
    const sp = new THREE.Sprite(mat);
    sp.center.set(0.5, 0.5);
    // Size bumped another +50% (was 9-14 wu → now 13-21 wu) so the
    // leaves are unmistakable at any zoom. Well above the grass tufts
    // (6-11 wu) in apparent size, but the brief alpha lifetime +
    // wind drift still read them as floating debris rather than
    // foliage clumps.
    const size = 13 + Math.random() * 8;
    sp.scale.set(size, size, 1);
    // Hover Y: well above terrain (hills top out near 14 + cell lift)
    // so leaves clearly fly over everything. renderOrder 70 places
    // them above the ocean (default) + grass but below entities so
    // armies / castles still draw on top.
    const baseY = 18 + Math.random() * 6;
    sp.position.set(fromX, baseY, fromZ);
    sp.renderOrder = 70;
    this.scene.add(sp);
    this.leaves.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration,
      fromX, fromZ,
      vx,
      vz,
      bobFreq: 1.2 + Math.random() * 1.4,
      bobAmp: 1.2 + Math.random() * 1.6,
      baseY,
      rotRate: (Math.random() - 0.5) * 2.6, // ±1.3 rad/s tumble
      rotPhase: Math.random() * Math.PI * 2,
    });
  }

  private updateDrops(): void {
    if (this.drops.length === 0) return;
    const t = this.clock.elapsedTime;
    const G = 200; // world units / s²
    for (const d of this.drops) {
      if (d.landedAt === 0) {
        const elapsed = t - d.startTime;
        const fallen = 0.5 * G * elapsed * elapsed;
        const y = 250 - fallen;
        if (y <= d.restingY) {
          d.sprite.position.set(d.x, d.restingY, d.z);
          d.landedAt = t;
        } else {
          d.sprite.position.set(d.x, y, d.z);
        }
      } else {
        const since = t - d.landedAt;
        d.sprite.position.y = d.restingY + Math.sin(since * 3) * 1.5;
      }
    }
  }

  private updateGhosts(): void {
    if (this.ghosts.length === 0) return;
    const t = this.clock.elapsedTime;
    for (let i = this.ghosts.length - 1; i >= 0; i--) {
      const g = this.ghosts[i]!;
      const elapsed = t - g.startTime;
      const u = Math.min(1, elapsed / g.duration);
      // Ease-out cubic so the army "settles" at the destination.
      const easedU = 1 - Math.pow(1 - u, 3);
      g.sprite.position.x = g.fromX + (g.toX - g.fromX) * easedU;
      g.sprite.position.z = g.fromZ + (g.toZ - g.fromZ) * easedU;
      // Half-sine arc for the hop.
      g.sprite.position.y = g.y + Math.sin(u * Math.PI) * g.hopHeight;

      // Dust-trail emission: spawn a tiny faction-coloured pixel under
      // the ghost every ~60 ms while it's airborne. The pixel rises a
      // little, then falls + fades — reads as "feet kicking up dust".
      // Only emit while u ∈ (0.05, 0.95) so the trail starts after the
      // hop kicks off and stops right before landing impact.
      if (
        g.trailColor !== undefined &&
        u > 0.05 && u < 0.95 &&
        t - (g.lastTrailEmit ?? 0) > 0.06
      ) {
        g.lastTrailEmit = t;
        this.spawnDustPixel(g.sprite.position.x, g.y, g.sprite.position.z, g.trailColor);
      }

      if (u >= 1) {
        this.scene.remove(g.sprite);
        g.sprite.material.dispose();
        g.onComplete?.();
        this.ghosts.splice(i, 1);
      }
    }
  }

  /**
   * Spawn a 2-second side-by-side melee clash at `at`. Two faction sprites
   * face each other, lunge in/out rhythmically, and the gap between them
   * pulses with impact bursts + tiny faction-coloured pixels — reads as
   * "a brutal little fight happening here". Both sprites despawn at the
   * end of the duration; combat outcome (death FX, fanfare, etc.) should
   * fire after this animation completes.
   */
  spawnMeleeClash(opts: {
    at: [number, number];
    attackerTexture: THREE.Texture;
    defenderTexture: THREE.Texture;
    attackerColor: number;
    defenderColor: number;
    durationMs?: number;
  }): void {
    const duration = (opts.durationMs ?? 2000) / 1000;
    const baseY = 5;
    const ASPECT = 480 / 260;
    const SCALE = 40;
    const SEP = 14; // separation at rest

    const makeSprite = (tex: THREE.Texture, flipX: boolean): THREE.Sprite => {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        alphaTest: 0.4,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      const sp = new THREE.Sprite(mat);
      sp.center.set(0.5, 0);
      // Negative X scale mirrors the sprite so opposing armies face each other.
      sp.scale.set(SCALE * ASPECT * (flipX ? -1 : 1), SCALE, 1);
      sp.renderOrder = 220;
      return sp;
    };
    const att = makeSprite(opts.attackerTexture, false); // faces right
    const def = makeSprite(opts.defenderTexture, true);  // faces left
    att.position.set(opts.at[0] - SEP, baseY, opts.at[1]);
    def.position.set(opts.at[0] + SEP, baseY, opts.at[1]);
    this.scene.add(att);
    this.scene.add(def);

    this.clashInstances.push({
      attackerSp: att,
      defenderSp: def,
      startTime: this.clock.elapsedTime,
      duration,
      centerX: opts.at[0],
      centerZ: opts.at[1],
      baseY,
      attackerColor: opts.attackerColor,
      defenderColor: opts.defenderColor,
      lastImpactT: 0,
    });
  }

  /**
   * Drive the melee-clash animation: each sprite lunges toward the centre
   * at a quick rhythm (~4.5 Hz), with periodic impact bursts + faction
   * pixels in the middle of the gap. The two sprites also shake slightly
   * on the Y axis so it doesn't read as a mechanical wobble.
   */
  private updateClashes(): void {
    if (this.clashInstances.length === 0) return;
    const t = this.clock.elapsedTime;
    const SEP = 14;
    const ASPECT = 480 / 260;
    const SCALE = 40;
    for (let i = this.clashInstances.length - 1; i >= 0; i--) {
      const c = this.clashInstances[i]!;
      const u = (t - c.startTime) / c.duration;
      if (u >= 1) {
        this.scene.remove(c.attackerSp);
        this.scene.remove(c.defenderSp);
        (c.attackerSp.material as THREE.SpriteMaterial).dispose();
        (c.defenderSp.material as THREE.SpriteMaterial).dispose();
        this.clashInstances.splice(i, 1);
        continue;
      }
      // Lunge: each sprite swings toward the centre with a fast oscillation.
      const tFromStart = t - c.startTime;
      const lunge = Math.sin(tFromStart * 4.5 * Math.PI * 2);   // -1..+1, ~4.5 Hz
      const wobble = Math.sin(tFromStart * 9.0 * Math.PI * 2) * 0.6; // y-jitter
      c.attackerSp.position.set(c.centerX - SEP + lunge * 4, c.baseY + wobble, c.centerZ);
      c.defenderSp.position.set(c.centerX + SEP - lunge * 4, c.baseY + wobble * 0.6, c.centerZ);
      // Preserve the mirrored X scale set at spawn (don't let an external
      // mutation accidentally flip the defender back).
      c.attackerSp.scale.set(SCALE * ASPECT, SCALE, 1);
      c.defenderSp.scale.set(-SCALE * ASPECT, SCALE, 1);
      // Periodic mini impact at the centre — ~every 220 ms. Alternates
      // attacker / defender colours so both factions' particles get tossed.
      if (t - c.lastImpactT > 0.22) {
        c.lastImpactT = t;
        const useAttacker = (Math.floor(t * 4) % 2) === 0;
        this.spawnImpactBurst(
          [c.centerX, c.centerZ],
          useAttacker ? c.attackerColor : c.defenderColor,
          { count: 6, spread: 0.55, baseY: c.baseY + 3 },
        );
      }
    }
  }

  /**
   * Tiny faction-coloured pixel emitted in the ghost's footstep wake. Sits
   * at ground level under the moving sprite, drifts up + sideways slightly,
   * and fades over ~500 ms. Plain colored square — no border, no shadow —
   * so it reads as a pixel-art puff of dust.
   */
  private spawnDustPixel(x: number, baseY: number, z: number, color: number): void {
    if (!this.whiteSquareTex) return;
    const mat = new THREE.SpriteMaterial({
      map: this.whiteSquareTex,
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sp = new THREE.Sprite(mat);
    sp.position.set(x + (Math.random() - 0.5) * 3, baseY + 0.5, z + (Math.random() - 0.5) * 3);
    const size = 1.5 + Math.random() * 1.5; // tiny: 1.5–3 wu pixels
    sp.scale.set(size, size, 1);
    sp.renderOrder = 200;
    this.scene.add(sp);
    // Drift slightly outward + upward, fading.
    this.particles.push({
      sprite: sp,
      startTime: this.clock.elapsedTime,
      duration: 0.5 + Math.random() * 0.25,
      startPos: [sp.position.x, sp.position.y, sp.position.z],
      velocity: [
        (Math.random() - 0.5) * 4,
        2 + Math.random() * 3,
        (Math.random() - 0.5) * 4,
      ],
      gravity: -6, // gentle settle
      startScale: size,
      endScale: size * 0.4,
    });
  }

  private handleResize(): void {
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.renderer.setSize(w, h);
    if (this.camera && this.map) {
      const aspect = w / h;
      const half = Math.max(this.map.width, this.map.height) * 0.55;
      this.camera.left = -half * aspect;
      this.camera.right = half * aspect;
      this.camera.top = half;
      this.camera.bottom = -half;
      this.camera.updateProjectionMatrix();
    }
  }

  /** Public hook — re-syncs the WebGL canvas to its host's current
   *  client size. Used by `mountInGame` when a previously-mounted
   *  renderer is reused after the host went display:none → display:''
   *  (the browser may have skipped firing a resize event in that case). */
  resizeToHost(): void {
    this.handleResize();
  }

  // --------------------------------------------------------------------------

  private buildScene(): void {
    if (!this.map || !this.fondTexture) return;
    // Preload the arrow projectile sprite so the first ranged attack
    // already uses the proper image instead of falling back to a square.
    void this.loadEntityTexture('/sprites/projectile_fleche.png');

    const positions: number[] = [];
    const uvs: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    let vOff = 0;

    // World units per tile. Native texture pixel size is no longer used —
    // we tile in fixed world space so cells of any size show the same grain.
    const TEX_SIZE = TERRAIN_TILE_WORLD_SIZE;

    // Convert sRGB hex region.color to linear-space RGB. Vertex colors live in
    // linear space; the GPU then encodes back to sRGB on output.
    const linColor = new THREE.Color();
    const linOf = (hex: number): { r: number; g: number; b: number } => {
      linColor.setHex(hex, THREE.SRGBColorSpace);
      return { r: linColor.r, g: linColor.g, b: linColor.b };
    };

    // ----- 1. Top faces (one platform per land cell, fan-triangulated) -----
    // Fan triangulation from cell.site is bullet-proof for Voronoi cells (the
    // site is by construction inside the polygon, even after Chaikin lifting),
    // and avoids any risk of earcut producing empty triangulations.
    for (const cell of this.map.cells) {
      if (cell.isOcean) continue;
      const poly = cell.displacedPolygon;
      if (poly.length < 3) continue;
      const region = this.map.regions[cell.regionId];
      if (!region) continue;

      const y = cellY(cell);
      // Ownership override wins over biome colour: when a region is owned, its
      // platform top wears the kingdom's banner instead of the biome tint.
      const fillHex = this.ownershipColors.get(region.id) ?? region.color;
      const c = linOf(fillHex);

      // Fan centre: the cell site, at the same Y as the rest of the platform.
      const centerIdx = vOff;
      positions.push(cell.site[0], y, cell.site[1]);
      uvs.push(cell.site[0] / TEX_SIZE, cell.site[1] / TEX_SIZE);
      colors.push(c.r, c.g, c.b);
      vOff++;

      // Polygon vertices
      const rimStart = vOff;
      for (const p of poly) {
        positions.push(p[0], y, p[1]);
        uvs.push(p[0] / TEX_SIZE, p[1] / TEX_SIZE);
        colors.push(c.r, c.g, c.b);
      }
      vOff += poly.length;

      // Triangles: (centre, rim[i], rim[i+1])
      for (let i = 0; i < poly.length; i++) {
        const next = (i + 1) % poly.length;
        indices.push(centerIdx, rimStart + i, rimStart + next);
      }
    }

    // ----- 2. Side walls between cells of different heights -----
    // For every unique edge, if its two adjacent cells sit at different Y, we
    // generate a vertical strip following the edge's polyline. Walls are
    // tinted with a darker shade of the higher cell's region colour so cliffs
    // look like extruded rock from that territory.
    for (const edge of this.map.edges) {
      const ca = edge.cellA >= 0 ? this.map.cells[edge.cellA] : null;
      const cb = edge.cellB >= 0 ? this.map.cells[edge.cellB] : null;
      if (!ca && !cb) continue;

      const yA = ca ? cellY(ca) : OCEAN_FLOOR_Y;
      const yB = cb ? cellY(cb) : OCEAN_FLOOR_Y;
      if (yA === yB) continue;

      const higherIsA = yA > yB;
      const higherCell = higherIsA ? ca : cb;
      // Walls only "belong" to land cells (we don't render submerged walls).
      if (!higherCell || higherCell.isOcean) continue;

      const yTop = higherIsA ? yA : yB;
      const yBot = higherIsA ? yB : yA;

      const region = this.map.regions[higherCell.regionId];
      if (!region) continue;
      // IMPORTANT: read the ownership override first (same as top faces).
      // Without this the cliff sides stayed in the region's GENERATOR color
      // instead of the conqueror's faction colour, so a captured territory
      // had a pink top with green walls — visually broken.
      const fillHex = this.ownershipColors.get(region.id) ?? region.color;
      const base = linOf(fillHex);
      // Top of cliff: ~85% brightness; bottom: ~50% — fakes ambient shading.
      const topR = base.r * 0.85, topG = base.g * 0.85, topB = base.b * 0.85;
      const botR = base.r * 0.5, botG = base.g * 0.5, botB = base.b * 0.5;

      const polyline = edge.polyline;
      // Cumulative distance along the polyline → smoothly tiled UVs across the
      // wall length, avoiding the "stretched texture on a slanted wall" artefact.
      const cum = [0];
      for (let i = 0; i < polyline.length - 1; i++) {
        const a = polyline[i]!;
        const b = polyline[i + 1]!;
        cum.push(cum[i]! + Math.hypot(b[0] - a[0], b[1] - a[1]));
      }

      for (let i = 0; i < polyline.length - 1; i++) {
        const p1 = polyline[i]!;
        const p2 = polyline[i + 1]!;
        const u1 = cum[i]! / TEX_SIZE;
        const u2 = cum[i + 1]! / TEX_SIZE;
        const vTop = yTop / TEX_SIZE;
        const vBot = yBot / TEX_SIZE;

        const v0 = vOff;
        // p1 top
        positions.push(p1[0], yTop, p1[1]);
        uvs.push(u1, vTop);
        colors.push(topR, topG, topB);
        // p2 top
        positions.push(p2[0], yTop, p2[1]);
        uvs.push(u2, vTop);
        colors.push(topR, topG, topB);
        // p2 bot
        positions.push(p2[0], yBot, p2[1]);
        uvs.push(u2, vBot);
        colors.push(botR, botG, botB);
        // p1 bot
        positions.push(p1[0], yBot, p1[1]);
        uvs.push(u1, vBot);
        colors.push(botR, botG, botB);
        vOff += 4;

        // Two triangles
        indices.push(v0, v0 + 1, v0 + 2);
        indices.push(v0, v0 + 2, v0 + 3);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();

    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      map: this.fondTexture,
      // DoubleSide saves us from worrying about wall winding orientation.
      side: THREE.DoubleSide,
    });
    this.terrainMesh = new THREE.Mesh(geo, mat);
    this.scene.add(this.terrainMesh);

    // ----- 3. Ocean plane (animated pixel-art shader) -----
    // Plane sized 3× the map so panning toward the edge still shows open
    // water. We bake a low-res distance-to-coast texture once per map and
    // sample it in the fragment shader to draw:
    //   • A narrow sandy beach apron where land meets water (dist ≈ 0..4 wu)
    //   • Animated foam wave fronts radiating outward from the coast
    //   • A calm pixel-art open ocean far from any land
    // Reference: pixel-art beach scenes with sand → wave foam → flat blue.
    const oceanW = this.map.width * 3;
    const oceanH = this.map.height * 3;
    // Moderate segment count — the surface is mostly flat (only a gentle
    // coastal ripple) so 96² ≈ 18k tris is plenty for soft undulations.
    const oceanGeo = new THREE.PlaneGeometry(oceanW, oceanH, 96, 96);
    // Bake a low-res distance-to-land mask (R8). Sampled by the fragment
    // shader to know how close each ocean pixel is to a continent.
    const COAST_MAX_DIST = 32.0;   // world units. Beyond this → fully open ocean.
    this.coastDistanceTex = this.bakeCoastDistance(COAST_MAX_DIST);
    const oceanMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime:     { value: 0 },
        uPixelSize:{ value: 4.0 },                       // pixel-art chunkiness
        // Brighter, sandier palette — closer to the coast wave colours so
        // the ocean integrates better with the cheerful pixel map.
        uSand:     { value: new THREE.Color(0xf2dca8) }, // bright sun-bleached sand
        uSandWet:  { value: new THREE.Color(0xd0a877) }, // damp sand at the surf line
        uShoreFoam:{ value: new THREE.Color(0xffffff) }, // pure white wave crests
        // The two ocean blues sit deliberately *very* close together so the
        // surface reads as one calm sea — no visible diagonal stripes.
        uShallow:  { value: new THREE.Color(0x76b8dc) }, // coast water (slightly lighter)
        uMid:      { value: new THREE.Color(0x6bb0d6) }, // open sea — barely darker
        uDeep:     { value: new THREE.Color(0x6bb0d6) }, // same as mid: kill far-ocean darkening
        uCoastTex: { value: this.coastDistanceTex },
        uMapOrigin:{ value: new THREE.Vector2(0, 0) },
        uMapSize:  { value: new THREE.Vector2(this.map.width, this.map.height) },
        uCoastMax: { value: COAST_MAX_DIST },
      },
      vertexShader: `
        precision highp float;
        varying vec3 vWorldPos;
        varying float vDist;
        uniform float uTime;
        uniform vec2 uMapOrigin;
        uniform vec2 uMapSize;
        uniform sampler2D uCoastTex;
        uniform float uCoastMax;

        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vec2 uv = (wp.xz - uMapOrigin) / uMapSize;
          float dist = uCoastMax;
          if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {
            dist = texture2D(uCoastTex, uv).r * uCoastMax;
          }
          // Subtle organic ripple — strong near coast, fades to flat at sea.
          // We can't do a vertex-displaced beach ramp because the ocean plane
          // is huge (3× map ≈ 5760 wu) and dense enough to resolve a 5-wu
          // beach would cost millions of tris. The sand is drawn as a
          // colour band in the fragment shader instead.
          float coastInfluence = 1.0 - smoothstep(0.0, 18.0, dist);
          float ripple = sin(wp.x * 0.05 + uTime * 1.6)
                       * cos(wp.z * 0.05 - uTime * 1.2);
          wp.y += ripple * coastInfluence * 0.30;
          vDist = dist;
          vWorldPos = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec3 vWorldPos;
        varying float vDist;
        uniform float uTime;
        uniform float uPixelSize;
        uniform vec3 uSand;
        uniform vec3 uSandWet;
        uniform vec3 uShoreFoam;
        uniform vec3 uShallow;
        uniform vec3 uMid;
        uniform vec3 uDeep;

        // 2D value-noise hash — gives organic, pixelated shimmer with no
        // visible horizontal stripe pattern.
        float hash2(vec2 p) {
          return fract(sin(dot(p, vec2(91.345, 47.853))) * 43758.5453);
        }

        void main() {
          // Pixel-quantize world position → blocky pixel-art look.
          vec2 p = floor(vWorldPos.xz / uPixelSize) * uPixelSize;
          float dist = vDist;

          // ---- Layer 1: bright calm open-ocean base ----
          // Per-block stable hash gives each "pixel" a personality; a slow
          // temporal cycle makes blocks shimmer independently. NO macro
          // wave / no diagonal pattern — the two blues sit so close to each
          // other that the surface reads as one uniform calm sea.
          float h = hash2(p * 0.05);
          float shimmer = sin(uTime * 0.45 + h * 6.283) * 0.5 + 0.5;
          // Two-tier staircase between two near-identical blues.
          float tier = step(0.6, shimmer);
          vec3 base = mix(uMid, uShallow, tier);

          vec3 col = base;

          // ---- Layer 2: animated foam wave fronts JUST off the coast ----
          // Concentric wave rings travel outward from the coast — these
          // sit on the *water* side (dist > 4.5) so they don't overprint
          // the sand band.
          if (dist > 4.5 && dist < 16.0) {
            float phase = sin(dist * 0.5 - uTime * 2.2);
            float foamMask = step(0.74, phase + (hash2(p * 0.5) - 0.5) * 0.15);
            float foamFade = 1.0 - smoothstep(4.5, 16.0, dist);
            // Constant inner foam line riding the surf edge.
            float shore = (1.0 - smoothstep(4.5, 6.5, dist));
            shore *= 0.55 + 0.45 * sin(p.x * 0.18 + p.y * 0.18 + uTime * 2.6);
            float foamAmt = clamp(foamMask * foamFade + shore, 0.0, 1.0);
            col = mix(col, uShoreFoam, foamAmt);
          }

          // ---- Layer 3: pixelated beach sand band hugging the coast ----
          // Sand follows the actual continent contour (the SDF was baked
          // from every land cell polygon), so the band wraps neatly around
          // peninsulas and bays. The band is now wider (≈ 6 wu) and carries
          // its OWN animated wave foam — pixelated white crests sliding
          // along the beach like small lapping waves.
          if (dist < 6.5) {
            // Quantize the sand colour at a coarser pixel size for a chunkier,
            // sandier look that contrasts with the smoother ocean shimmer.
            vec2 sandP = floor(vWorldPos.xz / (uPixelSize * 2.0)) * (uPixelSize * 2.0);
            float sandSeed = hash2(sandP * 0.13);
            // Per-block sand brightness wobble (warm / wet noise).
            float wobble = step(0.55, sandSeed);
            // Wet-vs-dry gradient based on distance from land edge.
            float wetT = smoothstep(0.0, 6.0, dist); // 0 at land, 1 outwards
            vec3 sand = mix(uSand, uSandWet, wetT);
            // Inject the brightness wobble (pixelated grain).
            sand = mix(sand, sand * 1.08, wobble);

            // Animated white wave on the sand: a pulsing stripe whose
            // distance-phase advances with time, so the foam appears to
            // wash up the beach. Pixelated step (no gradient).
            float beachWave = sin(dist * 1.6 - uTime * 2.6 + sandSeed * 0.8);
            float beachFoam = step(0.55, beachWave) * (1.0 - smoothstep(2.0, 6.0, dist));
            sand = mix(sand, uShoreFoam, beachFoam * 0.7);

            // Fade the sand band back into the ocean at its outer edge.
            float sandAmt = 1.0 - smoothstep(5.0, 6.5, dist);
            col = mix(col, sand, sandAmt);
          }

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    this.oceanMesh = new THREE.Mesh(oceanGeo, oceanMat);
    this.oceanMesh.rotation.x = -Math.PI / 2;
    this.oceanMesh.position.set(this.map.width / 2, OCEAN_FLOOR_Y, this.map.height / 2);
    this.scene.add(this.oceanMesh);
    this.oceanMaterial = oceanMat;

    // ----- 4. Borders + coastlines as 3D line segments at the higher cell's Y -----
    const coastPts: number[] = [];
    const innerPts: number[] = [];
    for (const edge of this.map.edges) {
      const cls = classifyEdge(edge, this.map);
      if (cls === 'skip') continue;
      const ca = edge.cellA >= 0 ? this.map.cells[edge.cellA] : null;
      const cb = edge.cellB >= 0 ? this.map.cells[edge.cellB] : null;
      // Render the line at the higher cell's elevation + small lift so it sits
      // exactly on the cliff lip.
      const yA = ca && !ca.isOcean ? cellY(ca) : OCEAN_FLOOR_Y;
      const yB = cb && !cb.isOcean ? cellY(cb) : OCEAN_FLOOR_Y;
      const y = Math.max(yA, yB) + 0.4;

      const target = cls === 'coast' ? coastPts : innerPts;
      const pl = edge.polyline;
      for (let i = 0; i < pl.length - 1; i++) {
        const a = pl[i]!;
        const b = pl[i + 1]!;
        target.push(a[0], y, a[1], b[0], y, b[1]);
      }
    }

    const coastGeo = new THREE.BufferGeometry();
    coastGeo.setAttribute('position', new THREE.Float32BufferAttribute(coastPts, 3));
    this.coastLines = new THREE.LineSegments(
      coastGeo,
      new THREE.LineBasicMaterial({ color: 0x100a06 }),
    );
    this.scene.add(this.coastLines);

    const innerGeo = new THREE.BufferGeometry();
    innerGeo.setAttribute('position', new THREE.Float32BufferAttribute(innerPts, 3));
    this.innerLines = new THREE.LineSegments(
      innerGeo,
      new THREE.LineBasicMaterial({ color: 0x352a1c, transparent: true, opacity: 0.65 }),
    );
    this.scene.add(this.innerLines);

    // ----- 5. Hover outline (geometry filled on hover) -----
    const hoverGeo = new THREE.BufferGeometry();
    hoverGeo.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    this.hoverLines = new THREE.LineSegments(
      hoverGeo,
      new THREE.LineBasicMaterial({
        color: 0xffe8a8,
        // Always render on top — never occluded by terrain or walls.
        depthTest: false,
        transparent: true,
      }),
    );
    this.hoverLines.renderOrder = 999;
    this.scene.add(this.hoverLines);

    // ----- 6. Action highlight: dashed faction color (top) over solid black (bottom).
    // The dashed top line creates gaps; the solid black underneath shows in
    // those gaps, giving us the alternating dashed black + colour effect.
    //
    // `depthTest: true` so the outline also gets occluded by higher
    // terrain (the line geometry now sits at the actual cell height —
    // see setActionHighlights). `depthWrite: false` keeps the line
    // from blocking sprites that draw afterwards.
    const actBlackGeo = new THREE.BufferGeometry();
    actBlackGeo.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    this.actionHighlightBlackLines = new THREE.LineSegments(
      actBlackGeo,
      new THREE.LineBasicMaterial({
        color: 0x000000,
        depthTest: true,
        depthWrite: false,
        transparent: true,
      }),
    );
    this.actionHighlightBlackLines.renderOrder = 96;
    this.scene.add(this.actionHighlightBlackLines);

    const actGeo = new THREE.BufferGeometry();
    actGeo.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    this.actionHighlightLines = new THREE.LineSegments(
      actGeo,
      new THREE.LineDashedMaterial({
        color: 0xff8a3c,
        depthTest: true,
        depthWrite: false,
        transparent: true,
        dashSize: 4,
        gapSize: 4,
        scale: 1,
      }),
    );
    this.actionHighlightLines.renderOrder = 97;
    this.scene.add(this.actionHighlightLines);
  }

  private setupCamera(): void {
    if (!this.map) return;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    const aspect = w / h;
    const half = Math.max(this.map.width, this.map.height) * 0.55;

    this.camera = new THREE.OrthographicCamera(
      -half * aspect,
      half * aspect,
      half,
      -half,
      -10000,
      10000,
    );

    const tilt = Math.PI / 4;
    const dist = Math.max(this.map.width, this.map.height);
    const cx = this.map.width / 2;
    const cz = this.map.height / 2;
    this.camera.position.set(cx, dist * Math.cos(tilt), cz + dist * Math.sin(tilt));
    this.camera.lookAt(cx, 0, cz);
  }

  private setupControls(): void {
    if (!this.map) return;
    if (this.controls) this.controls.dispose();
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(this.map.width / 2, 0, this.map.height / 2);

    // Lock the polar (vertical) angle: only horizontal rotation around the map.
    const tilt = Math.PI / 4;
    this.controls.minPolarAngle = tilt;
    this.controls.maxPolarAngle = tilt;

    // Mouse mapping per spec.
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    };

    // Touch mapping for mobile / tablet — mirror the mouse behaviour:
    // ONE finger = drag the map (pan), TWO fingers = pinch to zoom +
    // pan (DOLLY_PAN combines both). Default OrbitControls maps ONE
    // to rotate, which is wrong for our top-down strategy view since
    // we've locked the polar angle anyway.
    this.controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };

    this.controls.enableZoom = true;
    this.controls.zoomSpeed = 1.4;
    this.controls.panSpeed = 1.0;
    this.controls.rotateSpeed = 0.7;
    // FALSE → pan slides along the world's XZ ground plane (the 2.5D feel).
    // TRUE would pan in screen space (always perpendicular to the camera),
    // which feels like "moving a 2D image" instead of gliding over a tilted map.
    this.controls.screenSpacePanning = false;
    // minZoom 1.25 = clamps the dezoom so the player can't see way
    // beyond the playable map (was 0.85, which let users zoom out so
    // far they saw mostly empty ocean). The map itself fits at zoom
    // ≈ 1; 1.25 keeps roughly half a screen of ocean as breathing
    // room without making the map a postage stamp.
    this.controls.minZoom = 1.25;
    this.controls.maxZoom = 4;

    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;

    this.controls.update();
  }

  // --------------------------------------------------------------------------
  // Hover

  private handlePointer(e: PointerEvent): void {
    if (!this.map || !this.terrainMesh || !this.camera) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const hits = this.raycaster.intersectObject(this.terrainMesh, false);
    let region: Region | null = null;
    if (hits.length > 0) {
      const pt = hits[0]!.point;
      let bestIdx = -1;
      let bestD = Infinity;
      for (let i = 0; i < this.map.cells.length; i++) {
        const c = this.map.cells[i]!;
        const dx = c.site[0] - pt.x;
        const dy = c.site[1] - pt.z;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          bestIdx = i;
        }
      }
      const cell = bestIdx >= 0 ? this.map.cells[bestIdx] : null;
      if (cell && !cell.isOcean) region = this.map.regions[cell.regionId] ?? null;
    }

    const stats = document.getElementById('hover-debug');
    if (stats) {
      stats.textContent = region
        ? `cont=${region.continentId} reg=${region.id} biome=${region.biome}`
        : '';
    }

    if (region !== this.hoveredRegion) {
      this.hoveredRegion = region;
      this.updateHoverHighlight();
    }
  }

  /**
   * Treat a pointerup as a "click" only if the pointer hasn't moved more than
   * ~5 pixels since pointerdown. OrbitControls eats most pointer movement so
   * we can't rely on a global click handler — drags would fire it too.
   * `right=true` differentiates a right-click and dispatches to a separate
   * listener set so the game layer can offer different actions.
   */
  /**
   * Sample the alpha channel of a texture at the given UV. Caches an
   * ImageData snapshot of each texture in `alphaSamplers` so we only pay
   * the drawImage+getImageData cost once per texture (CanvasTexture or
   * loaded HTMLImage). Returns true when alpha ≥ 0.5.
   */
  private isPixelOpaque(tex: THREE.Texture, u: number, v: number): boolean {
    let sampler = this.alphaSamplers.get(tex);
    if (!sampler) {
      sampler = this.buildAlphaSampler(tex);
      this.alphaSamplers.set(tex, sampler);
    }
    return sampler(u, v) >= 0.5;
  }

  private buildAlphaSampler(tex: THREE.Texture): (u: number, v: number) => number {
    const src = tex.image as
      | HTMLImageElement
      | HTMLCanvasElement
      | ImageBitmap
      | null
      | undefined;
    if (!src) return () => 1;
    const w = (src as { width: number }).width;
    const h = (src as { height: number }).height;
    if (!w || !h) return () => 1;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const c2d = canvas.getContext('2d', { willReadFrequently: true });
    if (!c2d) return () => 1;
    c2d.imageSmoothingEnabled = false;
    try {
      c2d.drawImage(src as CanvasImageSource, 0, 0);
    } catch {
      return () => 1;
    }
    let pix: ImageData;
    try {
      pix = c2d.getImageData(0, 0, w, h);
    } catch {
      return () => 1;
    }
    const buf = pix.data;
    return (u: number, v: number): number => {
      // Three.js UVs: (0,0) bottom-left, (1,1) top-right. Image coords: top-left origin.
      const x = Math.max(0, Math.min(w - 1, Math.floor(u * w)));
      const y = Math.max(0, Math.min(h - 1, Math.floor((1 - v) * h)));
      return buf[(y * w + x) * 4 + 3]! / 255;
    };
  }

  private handleClick(e: PointerEvent, right: boolean): void {
    const down = right ? this.downPosRight : this.downPos;
    if (right) this.downPosRight = null;
    else this.downPos = null;
    if (!down || !this.map || !this.terrainMesh || !this.camera) return;
    const dx = e.clientX - down.x;
    const dy = e.clientY - down.y;
    if (dx * dx + dy * dy > 25) return; // > ~5px → drag, ignore
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    // Step 1: did we hit an entity SPRITE first? Left clicks on castles/armies
    // route to onEntityClick — but ONLY if the click landed on an OPAQUE pixel
    // of the sprite (per-pixel alpha hit test). Composite army textures have
    // ~50% transparent canvas around the body+weapons; without this filter,
    // clicking the empty area next to a tiny warrior would steal the click
    // from the region underneath.
    if (!right && this.entities.size > 0) {
      // Include satellites in the click-test pool so the tiny badges around an
      // army don't fall through to the terrain. Clicking a satellite routes
      // to the same region as the parent (its userData carries regionId).
      const allEntitySprites: THREE.Sprite[] = [];
      for (const inst of this.entities.values()) {
        allEntitySprites.push(inst.sprite);
        for (const s of inst.satellites) allEntitySprites.push(s);
      }
      const spriteHits = this.raycaster.intersectObjects(allEntitySprites, false);
      for (const hit of spriteHits) {
        const sp = hit.object as THREE.Sprite;
        const mat = sp.material as THREE.SpriteMaterial;
        const tex = mat.map;
        const uv = hit.uv;
        if (!tex || !uv) continue;
        if (!this.isPixelOpaque(tex, uv.x, uv.y)) continue; // transparent → skip, look at next hit
        const data = sp.userData as { kind?: 'army' | 'castle'; regionId?: number };
        if (data?.kind && typeof data.regionId === 'number') {
          for (const fn of this.entityClickListeners) {
            fn({ kind: data.kind, regionId: data.regionId });
          }
          return;
        }
      }
    }

    // Step 2: fall through to the terrain region underneath the click.
    const hits = this.raycaster.intersectObject(this.terrainMesh, false);
    if (hits.length === 0) return;
    const pt = hits[0]!.point;
    let bestIdx = -1;
    let bestD = Infinity;
    for (let i = 0; i < this.map.cells.length; i++) {
      const cellI = this.map.cells[i]!;
      const ddx = cellI.site[0] - pt.x;
      const ddy = cellI.site[1] - pt.z;
      const d = ddx * ddx + ddy * ddy;
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) {
      // Click missed any cell entirely — treat as "click outside".
      if (!right) for (const fn of this.emptyClickListeners) fn();
      return;
    }
    const cell = this.map.cells[bestIdx]!;
    if (cell.isOcean) {
      // Ocean clicks count as "click outside" so the game layer can
      // dismiss panels (region info, etc.).
      if (!right) for (const fn of this.emptyClickListeners) fn();
      return;
    }
    const set = right ? this.rightClickListeners : this.clickListeners;
    for (const fn of set) fn(cell.regionId);
  }

  private updateHoverHighlight(): void {
    if (!this.map || !this.hoverLines) return;
    const pts: number[] = [];
    if (this.hoveredRegion) {
      const found = this.hoveredRegion;
      for (const edge of this.map.edges) {
        const a = edge.cellA;
        const b = edge.cellB;
        if (a < 0 && b < 0) continue;
        const ca = a >= 0 ? this.map.cells[a] : null;
        const cb = b >= 0 ? this.map.cells[b] : null;
        const ra = ca?.regionId ?? -1;
        const rb = cb?.regionId ?? -1;
        const inRegion = ra === found.id || rb === found.id;
        const bothInRegion = ra === found.id && rb === found.id;
        if (!inRegion || bothInRegion) continue;
        // Highlight rides on top of the corresponding cliff lip.
        const yA = ca && !ca.isOcean ? cellY(ca) : OCEAN_FLOOR_Y;
        const yB = cb && !cb.isOcean ? cellY(cb) : OCEAN_FLOOR_Y;
        const y = Math.max(yA, yB) + 1.2;
        const pl = edge.polyline;
        for (let i = 0; i < pl.length - 1; i++) {
          const p1 = pl[i]!;
          const p2 = pl[i + 1]!;
          pts.push(p1[0], y, p1[1], p2[0], y, p2[1]);
        }
      }
    }

    // Replace the geometry cleanly (avoids relying on dispose+setAttribute order).
    const newGeo = new THREE.BufferGeometry();
    newGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    this.hoverLines.geometry.dispose();
    this.hoverLines.geometry = newGeo;
  }

  /**
   * Dispose only terrain + lines + the action highlight overlays. Leaves
   * grass, entities, ghosts and particles alone — they are either independent
   * of the terrain mesh or transient animations that must survive every
   * ownership patch. Used by `setOwnership`, which fires on every state
   * patch and otherwise would nuke every in-flight ghost/burst.
   */
  private disposeTerrainOnly(): void {
    const objs: Array<THREE.Mesh | THREE.LineSegments | null> = [
      this.terrainMesh,
      this.coastLines,
      this.innerLines,
      this.oceanMesh,
      this.hoverLines,
      this.actionHighlightLines,
      this.actionHighlightBlackLines,
      this.actionHighlightFill,
      this.actionHighlightOuterBorder,
    ];
    for (const obj of objs) {
      if (!obj) continue;
      this.scene.remove(obj);
      obj.geometry.dispose();
      const m = obj.material as THREE.Material | THREE.Material[];
      if (Array.isArray(m)) m.forEach((x) => x.dispose());
      else m.dispose();
    }
    this.terrainMesh = null;
    this.coastLines = null;
    this.innerLines = null;
    this.oceanMesh = null;
    this.hoverLines = null;
    this.actionHighlightLines = null;
    this.actionHighlightBlackLines = null;
    this.actionHighlightFill = null;
    this.actionHighlightOuterBorder = null;
    this.actionHighlightActive = false;
    this.hoveredRegion = null;
  }

  /**
   * Full teardown — terrain + grass + entities + transient animations.
   * Used by `setMap` when we switch to a completely new map.
   */
  /**
   * Rasterize the polygons of every "beach-eligible" cell to a low-res mask,
   * then run a 3-4 chamfer distance transform on it. The output is a single-
   * channel R8 `DataTexture` where each texel encodes the distance (in world
   * units, normalized by `maxDist`) from that point to the nearest
   * low-elevation coast.
   *
   * IMPORTANT: only cells with `cellY ≤ MAX_BEACH_HEIGHT` (currently 2 wu
   * → biome `coast`, `beach`, `swamp`) are rasterized. High cliffs
   * (plains/forest/hills/mountain/snow) are deliberately ignored so the
   * ocean shader does NOT draw a sand band or foam wave fronts under them
   * — cliffs plunge straight into the water with no surf.
   *
   * Sampled in the ocean shader to draw:
   *  • Sand apron where dist ≈ 0..5 wu
   *  • Animated foam wave fronts where dist ≈ 5..16 wu
   *  • Calm open-ocean elsewhere
   */
  private bakeCoastDistance(maxDist: number): THREE.DataTexture {
    const map = this.map!;
    // Aim for ~256 px on the long edge — plenty of resolution for a 0..32 wu
    // distance field, and trivial CPU cost (~65k cells × ~5 ops × 2 passes).
    const LONG = 256;
    const aspect = map.width / map.height;
    const W = aspect >= 1 ? LONG : Math.max(64, Math.round(LONG * aspect));
    const H = aspect >= 1 ? Math.max(64, Math.round(LONG / aspect)) : LONG;

    // Only flat, low-elevation cells produce a beach. Everything taller
    // (plains/forest/hills/mountain/snow) is a cliff straight into the sea.
    const MAX_BEACH_HEIGHT = 2;

    // --- Step 0: pick beach-eligible cells. -----------------------------------
    // A cell is beach-eligible if:
    //  • Its own elevation is ≤ MAX_BEACH_HEIGHT, OR
    //  • It is adjacent to a low cell (so the sand bank spans across a
    //    cluster of coastal cells and visually touches every neighbour —
    //    otherwise the band stops abruptly at higher cells and looks broken).
    const eligible = new Set<number>();
    for (const cell of map.cells) {
      if (cell.isOcean) continue;
      if (cellY(cell) <= MAX_BEACH_HEIGHT) {
        eligible.add(cell.index);
        for (const nIdx of cell.neighbors) {
          const n = map.cells[nIdx];
          if (n && !n.isOcean) eligible.add(nIdx);
        }
      }
    }

    // --- Step 1: rasterize beach-eligible land polygons into a binary mask. ---
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#fff';
    const sx = W / map.width;
    const sy = H / map.height;
    for (const cell of map.cells) {
      if (!eligible.has(cell.index)) continue;
      const poly = cell.displacedPolygon;
      if (poly.length < 3) continue;
      ctx.beginPath();
      ctx.moveTo(poly[0]![0] * sx, poly[0]![1] * sy);
      for (let i = 1; i < poly.length; i++) {
        ctx.lineTo(poly[i]![0] * sx, poly[i]![1] * sy);
      }
      ctx.closePath();
      ctx.fill();
    }
    const img = ctx.getImageData(0, 0, W, H);
    const isLand = new Uint8Array(W * H);
    const pixels = img.data;
    for (let i = 0; i < W * H; i++) {
      // Red channel ≥ 128 → land. (No need to test all channels: we fill solid white.)
      isLand[i] = (pixels[i * 4] ?? 0) >= 128 ? 1 : 0;
    }

    // --- Step 2: 3-4 chamfer distance transform. ---
    // Convention: orthogonal step = 3, diagonal = 4 → euclidean ≈ value / 3.
    const BIG = 1e9;
    const dist = new Float32Array(W * H);
    for (let i = 0; i < W * H; i++) dist[i] = isLand[i] ? 0 : BIG;

    // Forward pass: top-left to bottom-right. Use TypedArray-friendly
    // indexing — Float32Array indexed access is always `number`, but with
    // noUncheckedIndexedAccess the compiler still flags it, so we read into
    // a local first.
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = y * W + x;
        let d = dist[i] as number;
        if (d === 0) continue;
        if (y > 0) {
          const up = dist[(y - 1) * W + x] as number;
          if (up + 3 < d) d = up + 3;
          if (x > 0) {
            const ul = dist[(y - 1) * W + (x - 1)] as number;
            if (ul + 4 < d) d = ul + 4;
          }
          if (x < W - 1) {
            const ur = dist[(y - 1) * W + (x + 1)] as number;
            if (ur + 4 < d) d = ur + 4;
          }
        }
        if (x > 0) {
          const lf = dist[y * W + (x - 1)] as number;
          if (lf + 3 < d) d = lf + 3;
        }
        dist[i] = d;
      }
    }
    // Backward pass: bottom-right to top-left.
    for (let y = H - 1; y >= 0; y--) {
      for (let x = W - 1; x >= 0; x--) {
        const i = y * W + x;
        let d = dist[i] as number;
        if (d === 0) continue;
        if (y < H - 1) {
          const dn = dist[(y + 1) * W + x] as number;
          if (dn + 3 < d) d = dn + 3;
          if (x > 0) {
            const dl = dist[(y + 1) * W + (x - 1)] as number;
            if (dl + 4 < d) d = dl + 4;
          }
          if (x < W - 1) {
            const dr = dist[(y + 1) * W + (x + 1)] as number;
            if (dr + 4 < d) d = dr + 4;
          }
        }
        if (x < W - 1) {
          const rt = dist[y * W + (x + 1)] as number;
          if (rt + 3 < d) d = rt + 3;
        }
        dist[i] = d;
      }
    }

    // --- Step 3: pack into an R8 DataTexture. ---
    // dist is in chamfer units (3 per orthogonal pixel step). Convert to
    // world units via the pixel→world scale of the long axis, then normalize.
    const pixWorld = Math.max(map.width / W, map.height / H);
    const data = new Uint8Array(W * H * 4); // RGBA for broadest compatibility
    for (let i = 0; i < W * H; i++) {
      const chamfer = dist[i] as number;
      const worldDist = (chamfer / 3) * pixWorld;
      const v = Math.min(255, Math.round((worldDist / maxDist) * 255));
      data[i * 4 + 0] = v;
      data[i * 4 + 1] = v;
      data[i * 4 + 2] = v;
      data[i * 4 + 3] = 255;
    }
    const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat, THREE.UnsignedByteType);
    // Smooth ramp between sand/foam/ocean — bilinear filtering reads great
    // even at low mask resolution.
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Full teardown — drops every scene object AND releases the WebGL
   * context + removes the canvas from the DOM. Call this from the
   * outer in_game.ts when the player leaves a game OR when re-creating
   * the renderer for a new seed; otherwise the old canvas stays in
   * `#app` and its WebGL context keeps a slot in the browser's
   * ~16-context limit. Stack up enough leaks (e.g. multiple tutorial
   * runs) and `new WebGLRenderer()` starts failing with
   * "Could not create WebGL context".
   */
  dispose(): void {
    this.disposeScene();
    try {
      // Three's own teardown — releases programs, buffers, textures.
      this.renderer.dispose();
      // Explicitly force-loss the underlying GL context so the slot
      // is reclaimed immediately instead of waiting for GC. Without
      // this Chrome can hold the context alive for several seconds
      // after the canvas is removed, defeating the leak fix.
      const gl = this.renderer.getContext();
      const loseExt = gl?.getExtension('WEBGL_lose_context');
      loseExt?.loseContext();
    } catch { /* renderer already torn down */ }
    if (this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }
    if (this.controls) {
      try { this.controls.dispose(); } catch { /* noop */ }
      this.controls = null;
    }
  }

  private disposeScene(): void {
    this.disposeTerrainOnly();

    // Castle flags are map-scoped — drop them so a map switch (setMap)
    // doesn't leave the previous map's banners floating.
    for (const f of this.castleFlags.values()) {
      this.scene.remove(f.sprite);
      (f.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.castleFlags.clear();

    // Dispose grass: each Sprite owns a unique SpriteMaterial. Textures live
    // in spriteCache and are reused across regenerations — don't free them.
    for (const g of this.grassInstances) {
      this.scene.remove(g.sprite);
      g.sprite.material.dispose();
    }
    this.grassInstances = [];

    // Dispose every entity sprite + the registry, AND their satellites.
    for (const inst of this.entities.values()) {
      this.scene.remove(inst.sprite);
      inst.sprite.material.dispose();
      for (const s of inst.satellites) {
        this.scene.remove(s);
        (s.material as THREE.SpriteMaterial).dispose();
      }
    }
    this.entities.clear();

    // Dispose any in-flight ghosts.
    for (const g of this.ghosts) {
      this.scene.remove(g.sprite);
      g.sprite.material.dispose();
    }
    this.ghosts = [];

    // Dispose any in-flight melee clashes.
    for (const c of this.clashInstances) {
      this.scene.remove(c.attackerSp);
      this.scene.remove(c.defenderSp);
      (c.attackerSp.material as THREE.SpriteMaterial).dispose();
      (c.defenderSp.material as THREE.SpriteMaterial).dispose();
    }
    this.clashInstances = [];

    // Dispose any in-flight particles.
    for (const p of this.particles) {
      this.scene.remove(p.sprite);
      (p.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.particles = [];

    // Dispose any pending Pattée drops.
    for (const d of this.drops) {
      this.scene.remove(d.sprite);
      (d.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.drops = [];

    // Dispose any in-flight barbarian boats so they don't linger
    // across a game-end → new-game remount.
    for (const b of this.boats) {
      this.scene.remove(b.sprite);
      (b.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.boats = [];
    this.lastWakeAt = [];

    // Dispose transient capture-wave overlays + paw stamps (castle flags
    // + coast foam are already cleared at the top of disposeScene; the
    // shared foamTex / pawTex are renderer-lifetime and must NOT be
    // freed here or the next map's foam/stamps would render blank).
    for (const w of this.captureWaves) {
      this.scene.remove(w.mesh);
      w.mesh.geometry.dispose();
      w.material.dispose();
    }
    this.captureWaves = [];
    for (const s of this.pawStamps) {
      this.scene.remove(s.sprite);
      (s.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.pawStamps = [];

    // Dispose any in-flight snacalia jumps.
    for (const s of this.snacalias) {
      this.scene.remove(s.sprite);
      (s.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.snacalias = [];
    // Dispose any in-flight flying leaves.
    for (const l of this.leaves) {
      this.scene.remove(l.sprite);
      (l.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.leaves = [];
    // Dispose any in-flight ripple rings (geometry + material per ring).
    for (const r of this.rippleRings) {
      this.scene.remove(r.mesh);
      r.mesh.geometry.dispose();
      (r.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
    this.rippleRings = [];
    this.oceanMaterial = null;
    if (this.coastDistanceTex) {
      this.coastDistanceTex.dispose();
      this.coastDistanceTex = null;
    }
  }

  // --------------------------------------------------------------------------
  // Grass

  /**
   * Place clustered grass billboards on the platform tops. Each tuft is a
   * THREE.Sprite (camera-facing) with its own SpriteMaterial so we can swap
   * frames independently. Animation is driven by `updateGrass()` each frame.
   */
  private placeGrass(): void {
    if (!this.map) return;

    const simpleFrames = GRASS_FRAMES_SIMPLE.map(
      (n) => this.spriteCache.get(`/sprites/${n}`)!,
    );
    const doubleFrames = GRASS_FRAMES_DOUBLE.map(
      (n) => this.spriteCache.get(`/sprites/${n}`)!,
    );
    if (simpleFrames.some((t) => !t) || doubleFrames.some((t) => !t)) return;
    const simpleSeq = pingPongFrames(simpleFrames);
    const doubleSeq = pingPongFrames(doubleFrames);

    const rng = makeRng(this.map.seed ^ 0xc0ffee);

    let maxArea = 1;
    for (const r of this.map.regions) if (r.area > maxArea) maxArea = r.area;

    for (const region of this.map.regions) {
      const density = BIOME_DECO_DENSITY[region.biome];
      if (density === 0) continue;

      // World-space target HEIGHT for grass tufts. With UV-cropped textures
      // the sprite quad now contains the visible grass content directly, so
      // the size is the screen size of the grass itself. Range ~6 to 11
      // world units → ~6 to 11 screen pixels at 1:1 zoom.
      const norm = Math.sqrt(region.area / maxArea);
      const baseSize = 6 + norm * 5;

      // Tint: when the region is owned by a kingdom, the grass takes that
      // kingdom's banner colour (slightly darker). Falls back to the biome
      // tint for unowned/wild land. Built once per region, shared by every
      // tuft in that region.
      const tint = new THREE.Color();
      const ownerHex = this.ownershipColors.get(region.id);
      tint.setHex(ownerHex ?? region.color, THREE.SRGBColorSpace);
      tint.multiplyScalar(0.85);

      // Filter out coast cells: at a cliff edge, the line of sight from the
      // tilted camera passes the cliff, so the alpha-tested transparent areas
      // of the sprite reveal the OCEAN behind. The visual reads as "dark blue
      // ferns" — exactly the artefact reported.
      const allCells = this.map.cells;
      const interiorCells = region.cells.filter((ci) => {
        const c = allCells[ci];
        return c && !c.isCoast;
      });
      // Fall back to all cells if a region happens to be entirely coastal.
      const grassCells = interiorCells.length > 0 ? interiorCells : region.cells;

      const totalSprites = Math.max(1, Math.round((region.area / 1000) * density));
      const numClusters = Math.max(1, Math.round(totalSprites / 5));

      for (let cIdx = 0; cIdx < numClusters; cIdx++) {
        const cellIdx = grassCells[(rng() * grassCells.length) | 0];
        if (cellIdx === undefined) continue;
        const cell = this.map.cells[cellIdx];
        if (!cell) continue;

        // Tighten cluster centre and radius so sprites stay clearly inside the
        // cell — avoids spilling onto neighbour cells (different biome / lower
        // platform / ocean line-of-sight).
        const cx = cell.site[0] + (rng() - 0.5) * 3;
        const cz = cell.site[1] + (rng() - 0.5) * 3;
        const yBase = cellY(cell) + 0.05;

        const useDouble = rng() < 0.45;
        const seq = useDouble ? doubleSeq : simpleSeq;
        const aspect = useDouble ? GRASS_DOUBLE_ASPECT : GRASS_SIMPLE_ASPECT;
        const tufts = Math.max(2, 3 + ((rng() * 5) | 0));
        const clusterRadius = 5 + rng() * 4; // 5..9 (was 8..13)

        for (let i = 0; i < tufts; i++) {
          // Polar offset, sqrt-weighted radius for uniform area density.
          const angle = rng() * Math.PI * 2;
          const r = Math.sqrt(rng()) * clusterRadius;
          const x = cx + Math.cos(angle) * r;
          const z = cz + Math.sin(angle) * r;

          const startFrame = (rng() * seq.length) | 0;
          const mat = new THREE.SpriteMaterial({
            map: seq[startFrame],
            color: tint,
            // Pixel-art tufts: opaque-ish with hard alpha cutoff. Avoids the
            // tricky transparent-sorting issues of dense overlapping foliage.
            transparent: true,
            alphaTest: 0.5,
            depthWrite: true,
          });

          const sp = new THREE.Sprite(mat);
          // Anchor at bottom-centre so position.y == platform top.
          sp.center.set(0.5, 0);
          sp.position.set(x, yBase, z);
          const sizeJitter = 0.85 + rng() * 0.3;
          const finalH = baseSize * sizeJitter;
          // Width follows the cropped texture's aspect ratio so the sprite
          // doesn't squash/stretch the tuft (simple ≈ 1:1, double ≈ 1.29:1).
          sp.scale.set(finalH * aspect, finalH, 1);

          // Per-instance frame rate (4–7 fps) so tufts don't sway in lockstep.
          const fps = 4 + rng() * 3;
          const frameTime = 1 / fps;

          this.grassInstances.push({
            sprite: sp,
            frames: seq,
            frameTime,
            timeAccum: rng() * frameTime, // random phase
            currentFrame: startFrame,
            regionId: region.id,
            baseColorHex: region.color,
          });
          this.scene.add(sp);
        }
      }
    }
  }

  /**
   * Advance every grass instance's ping-pong animation by `dt` seconds.
   * Texture swap is just a JS property assignment; all frames are pre-uploaded
   * so the GPU never re-uploads. Three.js will pick the new texture at draw.
   */
  private updateGrass(dt: number): void {
    if (this.grassInstances.length === 0) return;
    for (const g of this.grassInstances) {
      g.timeAccum += dt;
      while (g.timeAccum >= g.frameTime) {
        g.timeAccum -= g.frameTime;
        g.currentFrame = (g.currentFrame + 1) % g.frames.length;
      }
      const tex = g.frames[g.currentFrame]!;
      const mat = g.sprite.material as THREE.SpriteMaterial;
      if (mat.map !== tex) mat.map = tex;
    }
  }
}

// ============================================================================

type EdgeClass = 'coast' | 'innerRegion' | 'skip';

function classifyEdge(edge: Edge, map: MapData): EdgeClass {
  const a = edge.cellA;
  const b = edge.cellB;
  if (a < 0 && b < 0) return 'skip';
  const ca = a >= 0 ? map.cells[a] : null;
  const cb = b >= 0 ? map.cells[b] : null;
  if (!ca || !cb) return 'skip';
  if (ca.isOcean && cb.isOcean) return 'skip';
  if (ca.isOcean !== cb.isOcean) return 'coast';
  if (ca.regionId === cb.regionId) return 'skip';
  return 'innerRegion';
}

/**
 * Linearly interpolate between two 0xRRGGBB colors in sRGB space. We stay in
 * sRGB (not linear) because the sprite materials run color-tinted through
 * their `mat.color` which the GPU multiplies in linear space — sRGB hex is
 * what THREE expects via `setHex`. `t` is clamped 0..1.
 */
function lerpHexColor(a: number, b: number, t: number): number {
  const u = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * u);
  const g = Math.round(ag + (bg - ag) * u);
  const bbb = Math.round(ab + (bb - ab) * u);
  return (r << 16) | (g << 8) | bbb;
}
